import { Platform } from 'react-native';
import AnalyticsService from './AnalyticsService';
import AsyncStorage from '../AsyncStorageShim';
import API_BASE_URL from '../config';

// MessageSource enum to match Swift code
export const MessageSource = {
    TWITTER: 'twitter',
    TELEGRAM: 'telegram',
    TIKTOK: 'tiktok',
    INSTAGRAM: 'instagram',
    FACEBOOK_GROUP: 'facebook_group',
    FACEBOOK_PAGE: 'facebook_page',
    WEBZ: 'webz',
    TV: 'tv'
};

// Define model interfaces to match Swift structs
/**
 * ListItem model
 */
class ListItem {
    id;
    name;
    request;
    notification_mode;
    notification_frequency;
    reaction_writing_style;
}

/**
 * TweetLink model
 */
class TweetLink {
    username;
    url;
    reason;
    tweet_content;
    translated_content;
    list_name;
    created_at;
    tweet_id;
    author_name;
    author_profile_image_url;
    verified_type;
    tweet_images;
    dependencies;
    source;

    // Calculate local created at date
    get localCreatedAt() {
        if (!this.created_at) return null;
        try {
            const date = new Date(this.created_at);
            return date.toLocaleString();
        } catch (error) {
            console.error("Error formatting date:", error);
            return this.created_at;
        }
    }
}

/**
 * Listener model
 */
class Listener {
    id;
    username;
    email;
    general_request;
}

/**
 * SuggestedList model
 */
class SuggestedList {
    id;
    list_name;
    list_prompt;
    list_image_url;
}

/**
 * SuggestedListDetails model
 */
class SuggestedListDetails {
    id;
    list_name;
    list_prompt;
    created_at;
    users;
}

/**
 * UserDetails model
 */
class UserDetails {
    username;
    name;
    profile_image_url;
}

/**
 * FollowDetails model
 */
class FollowDetails {
    username;
    is_removable;
    profile_image_url;
}

// Singleton implementation
class ListsManagerClass {
    static instance = null;

    fcmToken = null;
    cachedLanguage = null;

    baseUrl = API_BASE_URL;

    /**
     * Set the base URL for API requests
     * @param {string} url - The base URL
     */
    setBaseUrl(url) {
        // Remove trailing slash if present
        if (url) {
            this.baseUrl = url.replace(/\/$/, "");
            console.log(`📝 ListsManager: Base URL updated to ${this.baseUrl}`);
        }
    }

    /**
     * Initiate Twitter OAuth flow
     * @returns {Promise<{authorization_url: string}>}
     */
    async initiateXAuth() {
        try {
            AnalyticsService.logActivity('connect_x_account');
            const options = await this.getFetchOptions('GET');
            const response = await fetch(`${this.baseUrl}/auth/twitter/initiate`, options);

            if (this.handleAuthError(response)) return null;

            if (!response.ok) {
                const error = await response.json().catch(() => ({}));
                throw new Error(error.detail || `Failed to initiate Twitter auth: ${response.status}`);
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error("Error initiating Twitter auth:", error);
            throw error;
        }
    }

    /**
     * Get connected X (Twitter) accounts
     * @returns {Promise<{accounts: Array<Object>}>}
     */
    async getXAccounts() {
        try {
            const options = await this.getFetchOptions('GET');
            const response = await fetch(`${this.baseUrl}/auth/twitter/status`, options);

            if (this.handleAuthError(response)) return { accounts: [] };

            if (!response.ok) {
                const error = await response.json().catch(() => ({}));
                console.error(`Failed to get X accounts: ${response.status}`, error);
                return { accounts: [] };
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error("Error getting Twitter accounts:", error);
            return { accounts: [] };
        }
    }

    /**
     * Disconnect a specific X (Twitter) account
     * @param {string} xUsername - The Twitter username to disconnect
     * @returns {Promise<{success: boolean, message: string}>}
     */
    async disconnectXAccount(xUsername) {
        try {
            const options = await this.getFetchOptions('POST');
            const response = await fetch(`${this.baseUrl}/auth/twitter/disconnect?x_username=${encodeURIComponent(xUsername)}`, options);

            if (this.handleAuthError(response)) return null;

            if (!response.ok) {
                const error = await response.json().catch(() => ({}));
                throw new Error(error.detail || `Failed to disconnect Twitter account: ${response.status}`);
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error("Error disconnecting Twitter account:", error);
            throw error;
        }
    }

    /**
     * Post a tweet using a connected X account
     * @param {string} xUsername - The X account to post as
     * @param {Object} tweetData - The tweet payload (text, reply, quote_tweet_id, etc.)
     * @returns {Promise<{success: boolean, tweet: Object}>}
     */
    async postTweetAction(xUsername, tweetData) {
        try {
            AnalyticsService.logActivity('post_tweet', { x_username: xUsername });
            const payload = typeof tweetData === 'string'
                ? { x_username: xUsername, text: tweetData }
                : { x_username: xUsername, ...tweetData };

            const options = await this.getFetchOptions('POST', payload);
            const response = await fetch(`${this.baseUrl}/auth/twitter/action/tweet`, options);

            if (this.handleAuthError(response)) return null;

            if (!response.ok) {
                const errorText = await response.text().catch(() => '');
                throw new Error(`Failed to post tweet: ${response.status} ${errorText}`);
            }

            return await response.json();
        } catch (error) {
            console.error("Error posting tweet:", error);
            throw error;
        }
    }

    /**
     * Handler for authentication errors to redirect to login when token is invalid
     * @param {Response} response - The fetch response object
     * @returns {boolean} - True if an auth error was handled, false otherwise
     */
    handleAuthError(response) {
        if (response.status === 401) {
            console.error("❌ Authentication error: Token invalid or expired", {
                url: response.url,
                status: response.status,
            });

            // Sign out the user with Firebase v9
            (async () => {
                try {
                    const { getAuth, signOut } = await import('firebase/auth');
                    const auth = getAuth();

                    if (auth) {
                        await signOut(auth);
                        console.log("✅ User signed out due to auth error");
                    }
                } catch (error) {
                    console.error("❌ Error signing out:", error);
                }

                // Clear localStorage tokens
                if (typeof localStorage !== 'undefined') {
                    localStorage.removeItem('firebaseAuthToken');
                    localStorage.removeItem('firebaseAuthTokenExpiry');
                }

                // Clear AsyncStorage to break React Router infinite redirect loop
                try {
                    await AsyncStorage.removeItem('userToken');
                    await AsyncStorage.removeItem('name');
                    await AsyncStorage.removeItem('email');
                } catch (err) {
                    console.error("❌ Error clearing AsyncStorage:", err);
                }

                // Redirect to login page
                window.location.href = '/login';
            })();

            return true;
        }
        return false;
    }

    /**
     * Save Firebase token to localStorage for fallback
     * @param {string} token - The Firebase auth token to save
     */
    saveTokenToLocalStorage(token) {
        if (!token) return;

        try {
            if (typeof localStorage !== 'undefined') {
                localStorage.setItem('firebaseAuthToken', token);
                // Set expiry time (1 hour from now)
                const expiryTime = new Date().getTime() + (60 * 60 * 1000);
                localStorage.setItem('firebaseAuthTokenExpiry', expiryTime.toString());
            }
        } catch (error) {
            console.error("❌ Error saving token to localStorage:", error);
        }
    }

    /**
     * Get fetch options with appropriate headers including authorization
     * @param {string} method - HTTP method (GET, POST, etc.)
     * @param {Object} body - Request body for POST/PUT requests
     * @returns {Object} - Fetch options object
     */
    async getFetchOptions(method = 'GET', body = null) {
        // Get a fresh token for every request
        const token = await this.getAuthToken();

        // Save token to localStorage as fallback
        if (token) {
            this.saveTokenToLocalStorage(token);
        }

        const options = {
            method,
            mode: 'cors',
            credentials: 'omit',
            headers: {
                'Content-Type': 'application/json',
            }
        };

        // Add authorization header if token exists
        if (token) {
            options.headers['Authorization'] = `Bearer ${token}`;
        }

        // Add body for non-GET requests if provided
        if (body && method !== 'GET') {
            options.body = JSON.stringify(body);
        }

        return options;
    }

    /**
     * Parse a JSON list response and stamp the total match count (from the
     * X-Total-Count response header) onto the returned array as `.totalCount`.
     * The array still behaves like a normal array for existing callers; the
     * total reflects ALL matching rows server-side, not just this page.
     */
    async parseListWithTotal(response) {
        const arr = await response.json();
        if (Array.isArray(arr)) {
            const total = parseInt(response.headers.get('X-Total-Count'), 10);
            if (!Number.isNaN(total)) arr.totalCount = total;
        }
        return arr;
    }

    /**
     * Check if the current user is an admin
     */
    async isCurrentUserAdmin() {
        try {
            const options = await this.getFetchOptions();
            const response = await fetch(`${this.baseUrl}/listeners/is_admin`, options);

            if (this.handleAuthError(response)) {
                return false;
            }

            if (!response.ok) {
                console.error("❌ Error checking admin status: HTTP error");
                return false;
            }

            const data = await response.json();
            return data.is_admin || false;
        } catch (error) {
            console.error("❌ Error checking admin status:", error);
            return false;
        }
    }

    /**
     * TODO: FCM TOKEN FOR WEBSITE!
     * Send FCM token to backend for push notifications
     */
    async sendFCMTokenToBackend(token) {
        try {
            const options = await this.getFetchOptions('POST', {
                fcm_token: token
            });

            const response = await fetch(`${this.baseUrl}/listeners/save-fcm-token`, options);

            if (this.handleAuthError(response)) {
                return false;
            }

            if (response.ok) {
                console.log("✅ Successfully sent FCM token to backend");
                this.fcmToken = token;
                return true;
            } else {
                console.error("❌ Failed to send FCM token: Unexpected response");
                return false;
            }
        } catch (error) {
            console.error("❌ Failed to send FCM token:", error);
            return false;
        }
    }

    /**
     * Remove FCM token from backend
     */
    async removeFCMToken() {
        try {
            if (!this.fcmToken) {
                console.log("No FCM token found in memory, nothing to remove.");
                return true; // Return success since there's nothing to remove
            }

            const options = await this.getFetchOptions('POST', {
                fcm_token: this.fcmToken
            });

            const response = await fetch(`${this.baseUrl}/listeners/delete-fcm-token`, options);

            if (this.handleAuthError(response)) {
                return false;
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            // Clear the token from memory
            this.fcmToken = null;
            console.log("✅ FCM token removed successfully");

            // Track logout
            AnalyticsService.logActivity('logout');

            return true;
        } catch (error) {
            console.error("❌ Error removing FCM token:", error);
            return false;
        }
    }

    /**
     * Add a new listener
     */
    async addListener(listener) {
        try {
            const response = await fetch(`${this.baseUrl}/listeners/add`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(listener),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data.success || false;
        } catch (error) {
            console.error("Error adding listener:", error);
            return false;
        }
    }

    /**
     * Update listener language preference
     */
    async updateListenerLanguage(language) {
        try {
            const options = await this.getFetchOptions('POST', {
                save_language: language
            });

            const response = await fetch(`${this.baseUrl}/listeners/save-language`, options);

            if (this.handleAuthError(response)) {
                return false;
            }

            if (response.ok) {
                this.cachedLanguage = language; // Update cache
                AnalyticsService.logActivity('update_language', { language });
                return true;
            } else {
                return false;
            }
        } catch (error) {
            console.error("Error updating listener language:", error);
            return false;
        }
    }

    /**
     * Get listener's preferred language
     */
    async getListenerLanguage() {
        if (this.cachedLanguage) {
            return this.cachedLanguage;
        }

        try {
            const options = await this.getFetchOptions('GET');
            const response = await fetch(`${this.baseUrl}/listeners/get-language`, options);

            if (this.handleAuthError(response)) {
                return 'en'; // Default to English if auth error
            }

            if (response.ok) {
                const data = await response.json();
                const language = data.preferred_language || 'en';
                this.cachedLanguage = language; // Cache the result
                return language;
            } else {
                return 'en'; // Default to English if error
            }
        } catch (error) {
            console.error("Error getting listener language:", error);
            return 'en'; // Default to English if error
        }
    }

    /**
     * Fetch all lists for a listener
     */
    async fetchLists(orgId = null) {
        try {
            const options = await this.getFetchOptions();
            const url = orgId ? `${this.baseUrl}/lists?org_id=${orgId}` : `${this.baseUrl}/lists`;
            const response = await fetch(url, options);

            if (this.handleAuthError(response)) {
                return [];
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const lists = await response.json();

            // Track view lists
            AnalyticsService.logActivity('view_lists', { count: lists?.length || 0 });

            return lists;
        } catch (error) {
            console.error("Error fetching lists:", error);
            return [];
        }
    }

    /**
     * Fetch relevant TikToks from a specific list
     */
    async fetchRelevantTiktoksFromList(listID, since = null, limit = 20, offset = 0, search = null, sortBy = 'uploaded_at') {
        try {
            let url = `${this.baseUrl}/tiktoks/${listID}?limit=${limit}&offset=${offset}`;
            if (search && String(search).trim() !== '') {
                url += `&search=${encodeURIComponent(search.trim())}`;
            }
            if (sortBy) {
                url += `&sort_by=${encodeURIComponent(sortBy)}`;
            }
            if (since) {
                url += `&since=${encodeURIComponent(this.formatDateForAPI(since))}`;
            }

            if (offset === 0) {
                url += `&_=${new Date().getTime()}`;
            }

            const options = await this.getFetchOptions();
            const response = await fetch(url, options);

            if (this.handleAuthError(response)) {
                return [];
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await this.parseListWithTotal(response);
        } catch (error) {
            console.error("Error fetching TikToks from list:", error);
            return [];
        }
    }

    /**
     * Fetch all TikToks for a listener with pagination
     */
    async fetchAllTiktoksForListener(since = null, limit = 20, offset = 0, search = null, sortBy = null, filters = {}) {
        try {
            let url = `${this.baseUrl}/all_tiktoks?limit=${limit}&offset=${offset}`;
            if (search && String(search).trim() !== '') {
                url += `&search=${encodeURIComponent(search.trim())}`;
            }
            if (sortBy) {
                url += `&sort_by=${encodeURIComponent(sortBy)}`;
            }
            if (since) {
                url += `&since=${encodeURIComponent(this.formatDateForAPI(since))}`;
            }
            if (filters.emotion) url += `&emotion=${encodeURIComponent(filters.emotion)}`;
            if (filters.sentiment_min != null) url += `&sentiment_min=${filters.sentiment_min}`;
            if (filters.sentiment_max != null) url += `&sentiment_max=${filters.sentiment_max}`;
            if (filters.list_name) url += `&list_name=${encodeURIComponent(filters.list_name)}`;
            if (filters.list_role) url += `&list_role=${encodeURIComponent(filters.list_role)}`;
            if (filters.min_likes) url += `&min_likes=${filters.min_likes}`;
            if (filters.min_views) url += `&min_views=${filters.min_views}`;
            if (filters.org_id) url += `&org_id=${encodeURIComponent(filters.org_id)}`;

            if (offset === 0) {
                url += `&_=${new Date().getTime()}`;
            }

            const options = await this.getFetchOptions();
            const response = await fetch(url, options);

            if (this.handleAuthError(response)) {
                return [];
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await this.parseListWithTotal(response);
        } catch (error) {
            console.error("Error fetching all TikToks:", error);
            return [];
        }
    }

    /**
     * Fetch relevant tweets from a specific list
     * @param {string} listID - List identifier
     * @param {string|null} since - ISO date string to fetch messages since
     * @param {number} limit - Page size
     * @param {number} offset - Offset for pagination
     * @param {string|null} search - Optional search term to filter server side
     * @param {string} sortBy - Sort order ('created_at' or 'inserted_at')
     */
    async fetchRelevantTweetsFromList(listID, since = null, limit = 20, offset = 0, search = null, sortBy = 'created_at') {
        try {
            let url = `${this.baseUrl}/tweets/${listID}?limit=${limit}&offset=${offset}`;
            if (search && String(search).trim() !== '') {
                url += `&search=${encodeURIComponent(search.trim())}`;
            }
            if (sortBy) {
                url += `&sort_by=${encodeURIComponent(sortBy)}`;
            }
            if (since) {
                const formattedDate = this.formatDateForAPI(since);
                url += `&since=${encodeURIComponent(formattedDate)}`;
            }

            // Add cache busting for fresh results on initial load but allow caching for prefetch
            if (offset === 0) {
                url += `&_=${new Date().getTime()}`;
            }

            const options = await this.getFetchOptions();
            const response = await fetch(url, options);

            if (this.handleAuthError(response)) {
                return [];
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const tweets = await this.parseListWithTotal(response);

            // Track view list
            AnalyticsService.logActivity('view_list', {
                list_id: listID,
                has_search: !!search,
                sort_by: sortBy,
                source: 'twitter'
            });

            return tweets;
        } catch (error) {
            console.error("Error fetching tweets from list:", error);
            return [];
        }
    }

    /**
     * Fetch tweets for the authenticated listener with pagination
     * @param {string|null} since
     * @param {number} limit
     * @param {number} offset
     * @param {string|null} search - Optional search term
     * @param {string} sortBy - Sort order ('created_at' or 'inserted_at')
     */
    async fetchAllTweetsForListener(since = null, limit = 20, offset = 0, search = null, sortBy = 'created_at', filters = {}) {
        try {
            let url = `${this.baseUrl}/all_tweets?limit=${limit}&offset=${offset}`;
            if (search && String(search).trim() !== '') {
                url += `&search=${encodeURIComponent(search.trim())}`;
            }
            if (sortBy) {
                url += `&sort_by=${encodeURIComponent(sortBy)}`;
            }
            if (since) {
                const formattedDate = this.formatDateForAPI(since);
                url += `&since=${encodeURIComponent(formattedDate)}`;
            }
            if (filters.emotion) url += `&emotion=${encodeURIComponent(filters.emotion)}`;
            if (filters.sentiment_min != null) url += `&sentiment_min=${filters.sentiment_min}`;
            if (filters.sentiment_max != null) url += `&sentiment_max=${filters.sentiment_max}`;
            if (filters.list_name) url += `&list_name=${encodeURIComponent(filters.list_name)}`;
            if (filters.list_role) url += `&list_role=${encodeURIComponent(filters.list_role)}`;
            if (filters.min_likes) url += `&min_likes=${filters.min_likes}`;
            if (filters.min_views) url += `&min_views=${filters.min_views}`;
            if (filters.org_id) url += `&org_id=${encodeURIComponent(filters.org_id)}`;

            // Add cache busting for fresh results on initial load but allow caching for prefetch
            if (offset === 0) {
                url += `&_=${new Date().getTime()}`;
            }

            const options = await this.getFetchOptions();
            const response = await fetch(url, options);

            if (this.handleAuthError(response)) {
                return [];
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const tweets = await this.parseListWithTotal(response);
            return tweets;
        } catch (error) {
            console.error("Error fetching tweets:", error);
            return [];
        }
    }

    /**
     * Create a new list on the backend
     */
    async createListOnBackend(listName, request = "", reactionStyle = "", isWideMonitoringX = false, isWideMonitoringTiktok = false, isWideMonitoringInstagram = false, visibility = 'org', orgId = null, listRole = 'general') {
        try {
            const body = {
                list_name: listName,
                request: request || null,
                reaction_writing_style: reactionStyle || null,
                is_wide_monitoring: isWideMonitoringX,
                is_wide_monitoring_tiktok: isWideMonitoringTiktok,
                is_wide_monitoring_instagram: isWideMonitoringInstagram,
                visibility,
                org_id: orgId || null,
                list_role: listRole,
            };

            const options = await this.getFetchOptions('POST', body);
            const response = await fetch(`${this.baseUrl}/lists/create`, options);

            if (this.handleAuthError(response)) {
                return false;
            }

            if (!response.ok) {
                throw new Error(`Failed to create list: ${response.status}`);
            }

            const data = await response.json();
            console.log("List created successfully:", data);

            AnalyticsService.logActivity('create_list_success', {
                list_name: listName,
                has_prompt: !!request,
                is_wide_monitoring: isWideMonitoringX
            });

            return data.success || true;
        } catch (error) {
            console.error("Error creating list:", error);
            return false;
        }
    }

    /**
     * Generate an AI-powered list prompt based on list name and optional existing draft.
     * @param {string} listName - The name of the list.
     * @param {string} existingPrompt - Optional existing prompt draft.
     * @returns {Promise<string|null>} The generated prompt text, or null on failure.
     */
    async generateListPrompt(listName, existingPrompt = '') {
        try {
            const body = {
                list_name: listName,
                existing_prompt: existingPrompt || null,
            };

            const options = await this.getFetchOptions('POST', body);
            const response = await fetch(`${this.baseUrl}/lists/generate-prompt`, options);

            if (this.handleAuthError(response)) {
                return null;
            }

            if (!response.ok) {
                throw new Error(`Failed to generate prompt: ${response.status}`);
            }

            const data = await response.json();
            return data.prompt || null;
        } catch (error) {
            console.error("Error generating list prompt:", error);
            return null;
        }
    }

    /**
     * Remove a message from the feed (deletes from the relevant DB table)
     * @param {string} messageId - Platform-specific message ID (tweet_id / message_id / tiktok_id / instagram_id / fb_post_id)
     * @param {string} source - 'twitter' | 'telegram' | 'tiktok' | 'instagram' | 'facebook_group'
     */
    async removeMessage(messageId, source) {
        try {
            const options = await this.getFetchOptions('DELETE');
            const response = await fetch(
                `${this.baseUrl}/messages/${encodeURIComponent(messageId)}?source=${encodeURIComponent(source)}`,
                options
            );

            if (this.handleAuthError(response)) {
                return false;
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return true;
        } catch (error) {
            console.error("Error removing message:", error);
            return false;
        }
    }

    async movePostToList(messageId, source, targetListId) {
        try {
            const options = await this.getFetchOptions('PUT');
            const response = await fetch(
                `${this.baseUrl}/messages/move?message_id=${encodeURIComponent(messageId)}&source=${encodeURIComponent(source)}&target_list_id=${encodeURIComponent(targetListId)}`,
                options
            );

            if (this.handleAuthError(response)) {
                return false;
            }

            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.detail || `HTTP error! status: ${response.status}`);
            }

            return true;
        } catch (error) {
            console.error("Error moving post to list:", error);
            return false;
        }
    }

    async updateMessageFields(messageId, source, fields) {
        try {
            const options = await this.getFetchOptions('PATCH');
            options.headers['Content-Type'] = 'application/json';
            options.body = JSON.stringify(fields);
            const response = await fetch(
                `${this.baseUrl}/messages/${encodeURIComponent(messageId)}/fields?source=${encodeURIComponent(source)}`,
                options
            );

            if (this.handleAuthError(response)) {
                return false;
            }

            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                throw new Error(err.detail || `HTTP error! status: ${response.status}`);
            }

            return true;
        } catch (error) {
            console.error("Error updating message fields:", error);
            return false;
        }
    }

    /**
     * Delete a list
     */
    async deleteList(listID) {
        try {
            AnalyticsService.logActivity('delete_list', { list_id: listID });
            const options = await this.getFetchOptions('DELETE');
            const response = await fetch(`${this.baseUrl}/lists/${listID}`, options);

            if (this.handleAuthError(response)) {
                return false;
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return true;
        } catch (error) {
            console.error("Error deleting list:", error);
            return false;
        }
    }

    async transferListToOrg(listID, targetOrgId) {
        try {
            const options = await this.getFetchOptions('POST', { target_org_id: targetOrgId });
            const response = await fetch(`${this.baseUrl}/lists/${listID}/transfer-to-org`, options);

            if (this.handleAuthError(response)) {
                return { success: false, error: 'Auth error' };
            }

            const data = await response.json();
            if (!response.ok) {
                return { success: false, error: data.detail || 'Transfer failed' };
            }

            return { success: true };
        } catch (error) {
            console.error('Error transferring list:', error);
            return { success: false, error: error.message };
        }
    }

    /**
     * Fetch Twitter usernames from a list
     */
    async fetchTwitterUsernamesFromList(listID) {
        try {
            const options = await this.getFetchOptions();
            const response = await fetch(`${this.baseUrl}/follows/${listID}`, options);

            if (this.handleAuthError(response)) {
                return [];
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const follows = await response.json();
            return follows;
        } catch (error) {
            console.error("Error fetching Twitter usernames:", error);
            return [];
        }
    }

    /**
     * Fetch all Twitter usernames for a listener
     */
    async fetchAllTwitterUsernames() {
        try {
            const options = await this.getFetchOptions();
            const response = await fetch(`${this.baseUrl}/follows`, options);

            if (this.handleAuthError(response)) {
                return [];
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const follows = await response.json();
            return follows;
        } catch (error) {
            console.error("Error fetching all Twitter usernames:", error);
            return [];
        }
    }

    /**
     * Delete a user from a list
     */
    async deleteUserFromList(listID, username) {
        try {

            const requestBody = {
                list_id: listID,
                username: username,
            };

            const options = await this.getFetchOptions('DELETE', requestBody);
            const response = await fetch(`${this.baseUrl}/follows/delete`, options);

            if (this.handleAuthError(response)) {
                return false;
            }

            if (response.ok) {
                AnalyticsService.logActivity('remove_user_from_list', {
                    list_id: listID,
                    username: username,
                    platform: 'twitter'
                });
                return true;
            }
        } catch (error) {
            console.error("Error deleting user from list:", error);
            return false;
        }
    }

    /**
     * Fetch special request for a user
     */
    async fetchSpecialRequest(listID, username) {
        try {
            const options = await this.getFetchOptions();
            const response = await fetch(`${this.baseUrl}/follows/special-request/${listID}/${username}`, options);

            if (this.handleAuthError(response)) {
                return "";
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const specialRequest = await response.text();
            return specialRequest;
        } catch (error) {
            console.error("Error fetching special request:", error);
            return "";
        }
    }

    /**
     * Update list settings
     */
    async updateListSettings(listID, newName, newPrompt, newReactionStyle, isWideMonitoringX = undefined, isWideMonitoringTiktok = undefined, isWideMonitoringInstagram = undefined, visibility = undefined, listRole = undefined) {
        if (!listID || !newName || newPrompt === undefined) {
            console.error("Missing required parameters for updateListSettings");
            return false;
        }

        try {
            AnalyticsService.logActivity('update_list_settings', { list_id: listID });
            console.log(`Updating list settings for list ${listID}...`);

            const requestBody = {
                name: newName,
                prompt: newPrompt,
                reaction_writing_style: newReactionStyle
            };

            // Only include wide monitoring flags when explicitly provided
            if (isWideMonitoringX !== undefined) {
                requestBody.is_wide_monitoring = isWideMonitoringX;
            }
            if (isWideMonitoringTiktok !== undefined) {
                requestBody.is_wide_monitoring_tiktok = isWideMonitoringTiktok;
            }
            if (isWideMonitoringInstagram !== undefined) {
                requestBody.is_wide_monitoring_instagram = isWideMonitoringInstagram;
            }
            if (visibility !== undefined) {
                requestBody.visibility = visibility;
            }
            if (listRole !== undefined) {
                requestBody.list_role = listRole;
            }

            console.log("Request body:", JSON.stringify(requestBody));

            const options = await this.getFetchOptions('POST', requestBody);
            const response = await fetch(`${this.baseUrl}/lists/update-name-prompt/${listID}`, options);

            if (this.handleAuthError(response)) {
                return false;
            }

            if (!response.ok) {
                const errorText = await response.text();
                console.error(`Error updating list: HTTP ${response.status} - ${errorText}`);
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log("✅ List settings updated successfully");
            return data.success;
        } catch (error) {
            console.error("Error updating list settings:", error);
            return false;
        }
    }

    /**
     * Update special request for a user
     */
    async updateSpecialRequest(listID, username, specialRequest) {
        try {
            const options = await this.getFetchOptions('POST', {
                special_request: specialRequest,
            });

            const response = await fetch(`${this.baseUrl}/follows/update-special-request/${listID}/${username}`, options);

            if (this.handleAuthError(response)) {
                return false;
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            AnalyticsService.logActivity('save_special_request', {
                list_id: listID,
                username: username,
                request_length: specialRequest?.length || 0
            });

            return true;
        } catch (error) {
            console.error("Error updating special request:", error);
            return false;
        }
    }

    /**
     * Update notification settings for a list
     */
    async updateNotificationSettings(listID, notificationMode, notificationFrequency) {
        try {
            console.log("Updating notification settings:", {
                listID,
                notificationMode,
                notificationFrequency
            });

            // Create request body matching Swift implementation
            const requestBody = {
                "notification_mode": notificationMode,
                "notification_frequency": notificationFrequency
            };

            console.log("Request body:", JSON.stringify(requestBody));

            const options = await this.getFetchOptions('POST', requestBody);
            const response = await fetch(`${this.baseUrl}/lists/notification-settings/${listID}`, options);

            if (this.handleAuthError(response)) {
                return false;
            }

            if (!response.ok) {
                console.error(`HTTP error! status: ${response.status}`);
                const errorText = await response.text();
                console.error(`Response text: ${errorText}`);
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            console.log("Notification settings updated successfully");

            AnalyticsService.logActivity('update_notification_settings', {
                list_id: listID,
                mode: notificationMode,
                has_frequency: !!notificationFrequency
            });

            return true;
        } catch (error) {
            console.error("Error updating notification settings:", error);
            return false;
        }
    }

    /**
     * Follow a Twitter user
     */
    async followTwitterUser(username, specialRequest = null, listName = null, listID = null) {
        try {
            const body = {
                username: username,
                special_request: specialRequest,
                list_name: listName,
                list_id: listID,
            };

            const options = await this.getFetchOptions('POST', body);
            const response = await fetch(`${this.baseUrl}/follow/user`, options);

            if (this.handleAuthError(response)) {
                return false;
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data.success || response.ok) {
                AnalyticsService.logActivity('follow_user', {
                    list_id: listID,
                    username: username,
                    source: 'twitter'
                });
            }

            return data.success || false;
        } catch (error) {
            console.error("Error following Twitter user:", error);
            return false;
        }
    }

    /**
     * Follow multiple Twitter users
     */
    async followMultipleTwitterUsers(usernames, specialRequest = null, listName = null, listID = null) {
        try {
            AnalyticsService.logActivity('follow_multiple_users', { count: usernames?.length, list_name: listName });
            const body = {
                usernames: usernames,
                special_request: specialRequest,
                list_name: listName,
                list_id: listID,
            };

            const options = await this.getFetchOptions('POST', body);
            const response = await fetch(`${this.baseUrl}/follow/users`, options);

            if (this.handleAuthError(response)) {
                return false;
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data.success || false;
        } catch (error) {
            console.error("Error following multiple Twitter users:", error);
            return false;
        }
    }

    /**
     * Translate a tweet
     */
    async translateTweet(tweetContent, targetLanguage = "en") {
        try {
            AnalyticsService.logActivity('translate_content', { target_language: targetLanguage });
            const response = await fetch(`${this.baseUrl}/translate`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    tweet_content: tweetContent,
                    target_language: targetLanguage,
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data.translated_text || null;
        } catch (error) {
            console.error("Error translating tweet:", error);
            return null;
        }
    }

    /**
     * Fetch active narratives
     * @param {number} limit 
     * @param {number} offset 
     * @param {string} sortBy 
     */
    async fetchActiveNarratives(limit = 20, offset = 0, sortBy = "velocity_1h", orgId = null) {
        try {
            const options = await this.getFetchOptions();
            const orgParam = orgId ? `&org_id=${orgId}` : '';
            const response = await fetch(`${this.baseUrl}/narratives/active?limit=${limit}&offset=${offset}&sort_by=${sortBy}${orgParam}`, options);

            if (this.handleAuthError(response)) {
                return [];
            }

            if (!response.ok) {
                console.error(`HTTP error fetching active narratives! status: ${response.status}`);
                return [];
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error("Error fetching active narratives:", error);
            return [];
        }
    }

    /**
     * Fetch trending narratives
     * @param {number} limit 
     */
    async fetchTrendingNarratives(limit = 10, orgId = null) {
        try {
            const options = await this.getFetchOptions();
            const orgParam = orgId ? `&org_id=${orgId}` : '';
            const response = await fetch(`${this.baseUrl}/narratives/trending?limit=${limit}${orgParam}`, options);

            if (this.handleAuthError(response)) {
                return [];
            }

            if (!response.ok) {
                console.error(`HTTP error fetching trending narratives! status: ${response.status}`);
                return [];
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error("Error fetching trending narratives:", error);
            return [];
        }
    }

    /**
     * Fetch narrative details
     * @param {string} narrativeId 
     * @param {number} messageLimit 
     */
    async fetchNarrativeDetails(narrativeId, messageLimit = 50) {
        try {
            const options = await this.getFetchOptions();
            const response = await fetch(`${this.baseUrl}/narratives/${narrativeId}?message_limit=${messageLimit}`, options);

            if (this.handleAuthError(response)) {
                return null;
            }

            if (!response.ok) {
                console.error(`HTTP error fetching narrative details! status: ${response.status}`);
                return null;
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error("Error fetching narrative details:", error);
            return null;
        }
    }

    // =========================================================================
    // LISTENER PROFILE & AI REACTION METHODS
    // =========================================================================

    /**
     * Get the listener's writing style profile
     * @returns {Promise<Object|null>} Profile object with context, writing_style, has_profile
     */
    async getListenerProfile() {
        try {
            const options = await this.getFetchOptions();
            const response = await fetch(`${this.baseUrl}/reactions/profile`, options);

            if (this.handleAuthError(response)) {
                return null;
            }

            if (!response.ok) {
                console.error(`HTTP error fetching profile! status: ${response.status}`);
                return null;
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error("Error fetching listener profile:", error);
            return null;
        }
    }

    /**
     * Save/update the listener's writing style profile
     * @param {string|null} context - Company/personal context
     * @param {string|null} writingStyle - Writing style description
     * @param {string|null} brandScale - Brand scale: 'local', 'national', or 'global'
     * @returns {Promise<Object|null>} Updated profile object
     */
    async saveListenerProfile(context, writingStyle, brandScale = null) {
        try {
            const body = {
                context: context || null,
                writing_style: writingStyle || null,
            };
            if (brandScale) body.brand_scale = brandScale;

            const options = await this.getFetchOptions('POST', body);
            const response = await fetch(`${this.baseUrl}/reactions/profile`, options);

            if (this.handleAuthError(response)) {
                return null;
            }

            if (!response.ok) {
                console.error(`HTTP error saving profile! status: ${response.status}`);
                return null;
            }

            const data = await response.json();

            if (data) {
                AnalyticsService.logActivity('save_writing_style', {
                    has_context: !!context,
                    has_writing_style: !!writingStyle
                });
            }

            return data;
        } catch (error) {
            console.error("Error saving listener profile:", error);
            return null;
        }
    }

    /**
     * Check if the listener has a writing style profile set up
     * @returns {Promise<boolean>}
     */
    async hasListenerProfile() {
        const profile = await this.getListenerProfile();
        return profile?.has_profile || false;
    }

    /**
     * Generate an AI-powered reaction to a tweet/message
     * @param {string} tweetContent - The tweet content to react to
     * @param {string} tweetAuthor - The tweet author's username
     * @param {string|null} listName - Optional list name for context
     * @param {string|null} listRequest - Optional list request/prompt for context
     * @returns {Promise<Object>} Reaction response with reaction_text, success, error_message
     */
    async generateTweetReaction(tweetContent, tweetAuthor, listName = null, listRequest = null, listId = null, actionQueueGuidance = null, orgId = null) {
        try {
            const body = {
                tweet_content: tweetContent,
                tweet_author: tweetAuthor,
                list_name: listName,
                list_request: listRequest,
                list_id: listId,
            };
            if (actionQueueGuidance) body.action_queue_guidance = actionQueueGuidance;
            if (orgId) body.org_id = orgId;

            const options = await this.getFetchOptions('POST', body);
            const response = await fetch(`${this.baseUrl}/reactions/tweet`, options);

            if (this.handleAuthError(response)) {
                return { success: false, error_message: 'Authentication error' };
            }

            const data = await response.json();

            if (!response.ok) {
                return {
                    success: false,
                    error_message: data.detail || 'Failed to generate reaction'
                };
            }

            AnalyticsService.logActivity('generate_reaction', {
                list_id: listId,
                tweet_author: tweetAuthor,
                source: 'twitter'
            });

            return data;
        } catch (error) {
            console.error("Error generating tweet reaction:", error);
            return { success: false, error_message: error.message };
        }
    }

    /**
     * Generate an AI-powered thought leadership post about a trending narrative
     * @param {string} narrativeTitle - The narrative title
     * @param {string} narrativeSummary - The narrative summary
     * @param {Array<string>|null} sampleMessages - Optional sample messages from the narrative
     * @returns {Promise<Object>} Reaction response with reaction_text, success, error_message
     */
    async generateNarrativeReaction(narrativeId, listId = null, actionQueueGuidance = null) {
        try {
            AnalyticsService.logActivity('generate_narrative_reaction', { narrative_id: narrativeId });
            const body = {
                narrative_id: narrativeId,
            };

            // Add list_id if provided (dominant list for personalized reactions)
            if (listId) {
                body.list_id = listId;
            }
            if (actionQueueGuidance) body.action_queue_guidance = actionQueueGuidance;

            const options = await this.getFetchOptions('POST', body);
            const response = await fetch(`${this.baseUrl}/reactions/narrative_reaction`, options);

            if (this.handleAuthError(response)) {
                return { success: false, error_message: 'Authentication error' };
            }

            const data = await response.json();

            if (!response.ok) {
                return {
                    success: false,
                    error_message: data.detail || 'Failed to generate reaction'
                };
            }

            return data;
        } catch (error) {
            console.error("Error generating narrative reaction:", error);
            return { success: false, error_message: error.message };
        }
    }

    /**
 * Phase 1: Generate the counter-narrative strategy
 * (Formerly generateNarrativeReaction)
 * @param {string} narrativeId 
 * @param {Array<string>|null} selectedStrategies 
 * @returns {Promise<Object>} CounterNarrative response
 */
    async generateCounterNarrative(narrativeId, selectedStrategies = null) {
        try {
            AnalyticsService.logActivity('generate_counter_narrative', { narrative_id: narrativeId });
            const body = {
                narrative_id: narrativeId,
                selected_strategies: selectedStrategies || []
            };

            const options = await this.getFetchOptions('POST', body);
            // This endpoint now returns the full strategy + main post
            const response = await fetch(`${this.baseUrl}/reactions/narrative`, options);

            if (this.handleAuthError(response)) {
                return { success: false, error_message: 'Authentication error' };
            }

            const data = await response.json();

            if (!response.ok) {
                return {
                    success: false,
                    error_message: data.detail || 'Failed to generate counter-narrative'
                };
            }

            // Backend returns: { strategy_name, core_message, main_post, tone_guide, explanation }
            return { success: true, ...data };
        } catch (error) {
            console.error("Error generating counter-narrative:", error);
            return { success: false, error_message: error.message };
        }
    }

    async evaluateNarrativeAction(narrativeId) {
        try {
            const options = await this.getFetchOptions('POST', { narrative_id: narrativeId });
            const response = await fetch(`${this.baseUrl}/actions/evaluate-narrative`, options);
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error('Error evaluating narrative action:', error);
            return null;
        }
    }

    /**
     * Generate support posts (Phase 2)
     * Optional: Update the strategy itself if counterNarrativeId is provided
     * @param {string} coreMessage
     * @param {string} toneGuide
     * @param {number} count - Number of posts to generate
     * @param {string|null} counterNarrativeId - Optional ID of the counter-narrative to update
     * @param {string|null} mainPost - Optional updated main post for the strategy
     * @param {string|null} explanation - Optional updated explanation for the strategy
     * @returns {Promise<Object>} Response with posts array and success status
     */
    async generateSupportPosts(coreMessage, toneGuide, count = 20, counterNarrativeId = null, mainPost = null, explanation = null, strategyName = null) {
        try {
            const listenerLang = await this.getListenerLanguage();
            const body = {
                core_message: coreMessage,
                tone_guide: toneGuide,
                count: count,
                listener_language: listenerLang,
            };

            // Add optional update fields
            if (counterNarrativeId) {
                body.counter_narrative_id = counterNarrativeId;
                if (mainPost) body.main_post = mainPost;
                if (explanation) body.explanation = explanation;
                if (strategyName) body.strategy_name = strategyName;
            }

            const options = await this.getFetchOptions('POST', body);
            const response = await fetch(`${this.baseUrl}/reactions/narrative/support`, options);

            if (this.handleAuthError(response)) {
                return { success: false, error_message: "Authentication failed" };
            }

            if (!response.ok) {
                const errorData = await response.json();
                return { success: false, error_message: errorData.detail || "Failed to generate support posts" };
            }

            const result = await response.json();

            if (result.success || response.ok) {
                AnalyticsService.logActivity('generate_support_posts', {
                    narrative_id: counterNarrativeId,
                    post_count: count,
                    strategy_name: strategyName
                });
            }

            return result;
        } catch (error) {
            console.error("Error generating support posts:", error);
            return { success: false, error_message: error.message };
        }
    }

    /**
     * Fetch all saved counter-narrative strategies
     * @returns {Promise<Array<Object>>} Array of strategy objects
     */
    async fetchAllStrategies() {
        try {
            const options = await this.getFetchOptions('GET');
            const response = await fetch(`${this.baseUrl}/reactions/strategies`, options);

            if (this.handleAuthError(response)) {
                return [];
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error("Error fetching strategies:", error);
            return [];
        }
    }

    /**
     * Fetch strategies for a specific narrative
     * @param {string} narrativeId
     * @returns {Promise<Array<Object>>}
     */
    async fetchNarrativeStrategies(narrativeId) {
        try {
            const options = await this.getFetchOptions('GET');
            const response = await fetch(`${this.baseUrl}/reactions/narrative/${narrativeId}/strategies`, options);

            if (this.handleAuthError(response)) {
                return [];
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error("Error fetching narrative strategies:", error);
            return [];
        }
    }


    /**
     * Follow a Telegram channel
     */
    async followTelegramChannel(channelUsername, listName) {
        try {
            const body = {
                channel_username: channelUsername,
                list_name: listName,
            };

            const options = await this.getFetchOptions('POST', body);
            const response = await fetch(`${this.baseUrl}/telegram/follow`, options);

            if (this.handleAuthError(response)) {
                return false;
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            AnalyticsService.logActivity('follow_user', {
                list_id: listName, // Legacy usage of listName as ID in some places or just label
                username: channelUsername,
                source: 'telegram'
            });

            return true;
        } catch (error) {
            console.error("Error following Telegram channel:", error);
            return false;
        }
    }

    /**
     * Follow multiple Telegram channels
     */
    async followMultipleTelegramChannels(channelUsernames, listName) {
        try {
            AnalyticsService.logActivity('follow_multiple_users', { count: channelUsernames?.length, source: 'telegram', list_name: listName });
            const body = {
                channel_usernames: channelUsernames,
                list_name: listName,
            };

            const options = await this.getFetchOptions('POST', body);
            const response = await fetch(`${this.baseUrl}/telegram/follows`, options);

            if (this.handleAuthError(response)) {
                return false;
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data.success || false;
        } catch (error) {
            console.error("Error following multiple Telegram channels:", error);
            return false;
        }
    }

    /**
     * Get Telegram channels for a list
     */
    async getTelegramChannels(listID) {
        try {
            const options = await this.getFetchOptions();
            const response = await fetch(`${this.baseUrl}/telegram/follows/${listID}`, options);

            if (this.handleAuthError(response)) {
                return [];
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const channels = await response.json();
            return channels;
        } catch (error) {
            console.error("Error fetching Telegram channels:", error);
            return [];
        }
    }

    /**
     * Get all Telegram channels for a listener
     */
    async fetchAllTelegramChannels() {
        try {
            const options = await this.getFetchOptions();
            const response = await fetch(`${this.baseUrl}/telegram/channels`, options);

            if (this.handleAuthError(response)) {
                return [];
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const channels = await response.json();
            return channels;
        } catch (error) {
            console.error("Error fetching all Telegram channels:", error);
            return [];
        }
    }

    /**
     * Get Telegram messages for a list
     * @param {string} listID
     * @param {string|null} since
     * @param {number} limit
     * @param {number} offset
     * @param {string|null} search - Optional search term
     * @param {string} sortBy - Optional field to sort by (default: 'created_at')
     */
    async getTelegramMessages(listID, since = null, limit = 20, offset = 0, search = null, sortBy = 'created_at') {
        try {
            let url = `${this.baseUrl}/telegram/messages/${listID}?limit=${limit}&offset=${offset}`;
            if (search && String(search).trim() !== '') {
                url += `&search=${encodeURIComponent(search.trim())}`;
            }
            if (sortBy) {
                url += `&sort_by=${encodeURIComponent(sortBy)}`;
            }
            if (since) {
                url += `&since=${encodeURIComponent(since)}`;
            }

            // Add cache busting for fresh results on initial load but allow caching for prefetch
            if (offset === 0) {
                url += `&_=${new Date().getTime()}`;
            }

            const options = await this.getFetchOptions();
            const response = await fetch(url, options);

            if (this.handleAuthError(response)) {
                return [];
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const messages = await this.parseListWithTotal(response);

            // Track view list
            AnalyticsService.logActivity('view_list', {
                list_id: listID,
                has_search: !!search,
                sort_by: sortBy,
                source: 'telegram'
            });

            return messages;
        } catch (error) {
            console.error("Error fetching Telegram messages:", error);
            return [];
        }
    }

    /**
     * Delete a Telegram channel from a list
     */
    async deleteTelegramChannel(listID, channelUsername) {
        try {
            console.log(`Deleting Telegram channel ${channelUsername} from list ${listID}`);

            const requestBody = {
                list_id: listID,
                username: channelUsername,
            };

            console.log(`Request body: ${JSON.stringify(requestBody)}`);

            const options = await this.getFetchOptions('DELETE', requestBody);
            const response = await fetch(`${this.baseUrl}/telegram/follows/delete`, options);

            if (this.handleAuthError(response)) {
                return false;
            }

            console.log(`Response status: ${response.status}`);

            if (!response.ok) {
                const errorText = await response.text();
                console.error(`Error deleting channel: HTTP ${response.status} - ${errorText}`);
                throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
            }

            console.log(`✅ Successfully deleted Telegram channel ${channelUsername} from list ${listID}`);

            AnalyticsService.logActivity('remove_user_from_list', {
                list_id: listID,
                username: channelUsername,
                platform: 'telegram'
            });

            return true;
        } catch (error) {
            console.error("Error deleting Telegram channel:", error);
            return false;
        }
    }

    /**
     * Follow a TikTok channel
     */
    async followTikTokChannel(tiktokUsername, listName) {
        try {
            AnalyticsService.logActivity('follow_user', { username: tiktokUsername, source: 'tiktok', list_name: listName });
            const body = {
                tiktok_username: tiktokUsername,
                list_name: listName,
            };

            const options = await this.getFetchOptions('POST', body);
            const response = await fetch(`${this.baseUrl}/tiktok/follow`, options);

            if (this.handleAuthError(response)) {
                return false;
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data.success || false;
        } catch (error) {
            console.error("Error following TikTok channel:", error);
            return false;
        }
    }

    /**
     * Get TikTok channels for a list
     */
    async getTikTokChannels(listID) {
        try {
            const options = await this.getFetchOptions();
            const response = await fetch(`${this.baseUrl}/tiktok/follows/${listID}`, options);

            if (this.handleAuthError(response)) {
                return [];
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const channels = await response.json();
            return channels;
        } catch (error) {
            console.error("Error fetching TikTok channels:", error);
            return [];
        }
    }

    /**
     * Get all TikTok channels for a listener
     */
    async fetchAllTikTokChannels() {
        try {
            const options = await this.getFetchOptions();
            const response = await fetch(`${this.baseUrl}/tiktok/channels`, options);

            if (this.handleAuthError(response)) {
                return [];
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const channels = await response.json();
            return channels;
        } catch (error) {
            console.error("Error fetching all TikTok channels:", error);
            return [];
        }
    }

    /**
     * Delete a TikTok channel from a list
     */
    async deleteTikTokChannel(listID, tiktokUsername) {
        try {
            AnalyticsService.logActivity('remove_user_from_list', { list_id: listID, username: tiktokUsername, source: 'tiktok' });
            const requestBody = {
                list_id: listID,
                username: tiktokUsername,
            };

            const options = await this.getFetchOptions('DELETE', requestBody);
            const response = await fetch(`${this.baseUrl}/tiktok/follows/delete`, options);

            if (this.handleAuthError(response)) {
                return false;
            }

            if (!response.ok) {
                const errorText = await response.text();
                console.error(`Error deleting TikTok channel: HTTP ${response.status} - ${errorText}`);
                throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
            }

            return true;
        } catch (error) {
            console.error("Error deleting TikTok channel:", error);
            return false;
        }
    }

    /**
     * Fetch relevant Instagrams from a specific list
     */
    async fetchRelevantInstagramsFromList(listID, since = null, limit = 20, offset = 0, search = null, sortBy = 'uploaded_at') {
        try {
            let url = `${this.baseUrl}/instagrams/${listID}?limit=${limit}&offset=${offset}`;
            if (search && String(search).trim() !== '') {
                url += `&search=${encodeURIComponent(search.trim())}`;
            }
            if (sortBy) {
                url += `&sort_by=${encodeURIComponent(sortBy)}`;
            }
            if (since) {
                url += `&since=${encodeURIComponent(this.formatDateForAPI(since))}`;
            }

            if (offset === 0) {
                url += `&_=${new Date().getTime()}`;
            }

            const options = await this.getFetchOptions();
            const response = await fetch(url, options);

            if (this.handleAuthError(response)) {
                return [];
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await this.parseListWithTotal(response);
        } catch (error) {
            console.error("Error fetching Instagrams from list:", error);
            return [];
        }
    }

    /**
     * Fetch all Instagrams for a listener with pagination
     */
    async fetchAllInstagramsForListener(since = null, limit = 20, offset = 0, search = null, sortBy = null, filters = {}) {
        try {
            let url = `${this.baseUrl}/all_instagrams?limit=${limit}&offset=${offset}`;
            if (search && String(search).trim() !== '') {
                url += `&search=${encodeURIComponent(search.trim())}`;
            }
            if (sortBy) {
                url += `&sort_by=${encodeURIComponent(sortBy)}`;
            }
            if (since) {
                url += `&since=${encodeURIComponent(this.formatDateForAPI(since))}`;
            }
            if (filters.emotion) url += `&emotion=${encodeURIComponent(filters.emotion)}`;
            if (filters.sentiment_min != null) url += `&sentiment_min=${filters.sentiment_min}`;
            if (filters.sentiment_max != null) url += `&sentiment_max=${filters.sentiment_max}`;
            if (filters.list_name) url += `&list_name=${encodeURIComponent(filters.list_name)}`;
            if (filters.list_role) url += `&list_role=${encodeURIComponent(filters.list_role)}`;
            if (filters.min_likes) url += `&min_likes=${filters.min_likes}`;
            if (filters.org_id) url += `&org_id=${encodeURIComponent(filters.org_id)}`;

            if (offset === 0) {
                url += `&_=${new Date().getTime()}`;
            }

            const options = await this.getFetchOptions();
            const response = await fetch(url, options);

            if (this.handleAuthError(response)) {
                return [];
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await this.parseListWithTotal(response);
        } catch (error) {
            console.error("Error fetching all Instagrams:", error);
            return [];
        }
    }

    async fetchFacebookGroupPostsFromList(listID, since = null, limit = 20, offset = 0, search = null, sortBy = 'uploaded_at') {
        try {
            let url = `${this.baseUrl}/facebook_groups/${listID}?limit=${limit}&offset=${offset}`;
            if (search && String(search).trim() !== '') {
                url += `&search=${encodeURIComponent(search.trim())}`;
            }
            if (sortBy) {
                url += `&sort_by=${encodeURIComponent(sortBy)}`;
            }
            if (since) {
                url += `&since=${encodeURIComponent(this.formatDateForAPI(since))}`;
            }
            if (offset === 0) {
                url += `&_=${new Date().getTime()}`;
            }
            const options = await this.getFetchOptions();
            const response = await fetch(url, options);
            if (this.handleAuthError(response)) return [];
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            return await this.parseListWithTotal(response);
        } catch (error) {
            console.error("Error fetching Facebook group posts from list:", error);
            return [];
        }
    }

    async fetchAllFacebookGroupPostsForListener(since = null, limit = 20, offset = 0, search = null, sortBy = null, filters = {}) {
        try {
            let url = `${this.baseUrl}/all_facebook_groups?limit=${limit}&offset=${offset}`;
            if (search && String(search).trim() !== '') {
                url += `&search=${encodeURIComponent(search.trim())}`;
            }
            if (sortBy) {
                url += `&sort_by=${encodeURIComponent(sortBy)}`;
            }
            if (since) {
                url += `&since=${encodeURIComponent(this.formatDateForAPI(since))}`;
            }
            if (filters.emotion) url += `&emotion=${encodeURIComponent(filters.emotion)}`;
            if (filters.sentiment_min != null) url += `&sentiment_min=${filters.sentiment_min}`;
            if (filters.sentiment_max != null) url += `&sentiment_max=${filters.sentiment_max}`;
            if (filters.list_name) url += `&list_name=${encodeURIComponent(filters.list_name)}`;
            if (filters.list_role) url += `&list_role=${encodeURIComponent(filters.list_role)}`;
            if (filters.org_id) url += `&org_id=${encodeURIComponent(filters.org_id)}`;
            if (offset === 0) {
                url += `&_=${new Date().getTime()}`;
            }
            const options = await this.getFetchOptions();
            const response = await fetch(url, options);
            if (this.handleAuthError(response)) return [];
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            return await this.parseListWithTotal(response);
        } catch (error) {
            console.error("Error fetching all Facebook group posts:", error);
            return [];
        }
    }

    /**
     * Follow an Instagram profile
     */
    async followInstagramProfile(instagramUsername, listName) {
        try {
            AnalyticsService.logActivity('follow_user', { username: instagramUsername, source: 'instagram', list_name: listName });
            const body = {
                instagram_username: instagramUsername,
                list_name: listName,
            };

            const options = await this.getFetchOptions('POST', body);
            const response = await fetch(`${this.baseUrl}/instagram/follow`, options);

            if (this.handleAuthError(response)) {
                return false;
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data.success || false;
        } catch (error) {
            console.error("Error following Instagram profile:", error);
            return false;
        }
    }

    /**
     * Get Instagram profiles for a list
     */
    async getInstagramProfiles(listID) {
        try {
            const options = await this.getFetchOptions();
            const response = await fetch(`${this.baseUrl}/instagram/follows/${listID}`, options);

            if (this.handleAuthError(response)) {
                return [];
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const profiles = await response.json();
            return profiles;
        } catch (error) {
            console.error("Error fetching Instagram profiles:", error);
            return [];
        }
    }

    /**
     * Get all Instagram profiles for a listener
     */
    async fetchAllInstagramProfiles() {
        try {
            const options = await this.getFetchOptions();
            const response = await fetch(`${this.baseUrl}/instagram/profiles`, options);

            if (this.handleAuthError(response)) {
                return [];
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const profiles = await response.json();
            return profiles;
        } catch (error) {
            console.error("Error fetching all Instagram profiles:", error);
            return [];
        }
    }

    /**
     * Delete an Instagram profile from a list
     */
    async deleteInstagramProfile(listID, instagramUsername) {
        try {
            AnalyticsService.logActivity('remove_user_from_list', { list_id: listID, username: instagramUsername, source: 'instagram' });
            const requestBody = {
                list_id: listID,
                username: instagramUsername,
            };

            const options = await this.getFetchOptions('DELETE', requestBody);
            const response = await fetch(`${this.baseUrl}/instagram/follows/delete`, options);

            if (this.handleAuthError(response)) {
                return false;
            }

            if (!response.ok) {
                const errorText = await response.text();
                console.error(`Error deleting Instagram profile: HTTP ${response.status} - ${errorText}`);
                throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
            }

            return true;
        } catch (error) {
            console.error("Error deleting Instagram profile:", error);
            return false;
        }
    }

    // ============ Facebook Page follow methods ============
    // Followed pages drive the Apify Facebook Pages scraper. The resulting posts are stored
    // alongside Facebook group posts, so they surface through the existing Facebook feed.

    /**
     * Follow a Facebook page (handle or URL)
     */
    async followFacebookPage(page, listName) {
        try {
            AnalyticsService.logActivity('follow_user', { username: page, source: 'facebook_page', list_name: listName });
            const body = {
                page: page,
                list_name: listName,
            };

            const options = await this.getFetchOptions('POST', body);
            const response = await fetch(`${this.baseUrl}/facebook_page/follow`, options);

            if (this.handleAuthError(response)) {
                return false;
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data.success || false;
        } catch (error) {
            console.error("Error following Facebook page:", error);
            return false;
        }
    }

    /**
     * Get Facebook pages for a list
     */
    async getFacebookPages(listID) {
        try {
            const options = await this.getFetchOptions();
            const response = await fetch(`${this.baseUrl}/facebook_page/follows/${listID}`, options);

            if (this.handleAuthError(response)) {
                return [];
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error("Error fetching Facebook pages:", error);
            return [];
        }
    }

    /**
     * Get all Facebook pages for a listener
     */
    async fetchAllFacebookPages() {
        try {
            const options = await this.getFetchOptions();
            const response = await fetch(`${this.baseUrl}/facebook_page/pages`, options);

            if (this.handleAuthError(response)) {
                return [];
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            return await response.json();
        } catch (error) {
            console.error("Error fetching all Facebook pages:", error);
            return [];
        }
    }

    /**
     * Delete a Facebook page from a list
     */
    async deleteFacebookPage(listID, page) {
        try {
            AnalyticsService.logActivity('remove_user_from_list', { list_id: listID, username: page, source: 'facebook_page' });
            const requestBody = {
                list_id: listID,
                username: page,
            };

            const options = await this.getFetchOptions('DELETE', requestBody);
            const response = await fetch(`${this.baseUrl}/facebook_page/follows/delete`, options);

            if (this.handleAuthError(response)) {
                return false;
            }

            if (!response.ok) {
                const errorText = await response.text();
                console.error(`Error deleting Facebook page: HTTP ${response.status} - ${errorText}`);
                throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
            }

            return true;
        } catch (error) {
            console.error("Error deleting Facebook page:", error);
            return false;
        }
    }

    /**
     * Get all Telegram messages for a listener with pagination
     * @param {string|null} since
     * @param {number} limit
     * @param {number} offset
     * @param {string|null} search - Optional search term
     * @param {string} sortBy - Optional field to sort by (default: 'created_at')
     */
    async fetchAllTelegramMessagesForListener(since = null, limit = 20, offset = 0, search = null, sortBy = 'created_at', filters = {}) {
        try {
            let url = `${this.baseUrl}/all_telegram_messages?limit=${limit}&offset=${offset}`;
            if (search && String(search).trim() !== '') {
                url += `&search=${encodeURIComponent(search.trim())}`;
            }
            if (sortBy) {
                url += `&sort_by=${encodeURIComponent(sortBy)}`;
            }
            if (since) {
                url += `&since=${encodeURIComponent(since)}`;
            }
            if (filters.emotion) url += `&emotion=${encodeURIComponent(filters.emotion)}`;
            if (filters.sentiment_min != null) url += `&sentiment_min=${filters.sentiment_min}`;
            if (filters.sentiment_max != null) url += `&sentiment_max=${filters.sentiment_max}`;
            if (filters.list_name) url += `&list_name=${encodeURIComponent(filters.list_name)}`;
            if (filters.list_role) url += `&list_role=${encodeURIComponent(filters.list_role)}`;
            if (filters.org_id) url += `&org_id=${encodeURIComponent(filters.org_id)}`;

            // Add cache busting for fresh results on initial load but allow caching for prefetch
            if (offset === 0) {
                url += `&_=${new Date().getTime()}`;
            }

            const options = await this.getFetchOptions();
            const response = await fetch(url, options);

            if (this.handleAuthError(response)) {
                return [];
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const messages = await this.parseListWithTotal(response);
            return messages;
        } catch (error) {
            console.error("Error fetching all Telegram messages:", error);
            return [];
        }
    }

    /**
     * Fetch suggested lists for a listener
     */
    async fetchSuggestedLists() {
        try {
            const options = await this.getFetchOptions();
            const response = await fetch(`${this.baseUrl}/get_suggested_lists_for_listener`, options);

            if (this.handleAuthError(response)) {
                return [];
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const lists = await response.json();
            return lists;
        } catch (error) {
            console.error("Error fetching suggested lists:", error);
            return [];
        }
    }

    /**
     * Fetch details for a specific suggested list
     */
    async fetchSuggestedListDetails(suggestedListID) {
        try {
            const options = await this.getFetchOptions();
            const response = await fetch(`${this.baseUrl}/get_suggested_lists_details/${suggestedListID}`, options);

            // Check for auth errors
            if (this.handleAuthError(response)) {
                return null;
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const details = await response.json();
            return details; // Return the object directly
        } catch (error) {
            console.error("Error fetching suggested list details:", error);
            return null; // Return null on error
        }
    }

    /**
     * Process a suggested list on the backend
     * @param {string} suggestedListID - The ID of the suggested list
     * @param {string} customName - Optional custom name for the list
     * @param {string} prompt - Optional custom prompt for the list
     * @returns {Promise<boolean>} - Whether the operation was successful
     */
    async processSuggestedListOnBackend(suggestedListID, customName, prompt) {
        try {
            AnalyticsService.logActivity('adopt_suggested_list', { suggested_list_id: suggestedListID, custom_name: customName });
            const requestBody = {
                suggested_list_id: suggestedListID
            };

            // Add customName to request if provided
            if (customName && customName.trim() !== '') {
                requestBody.custom_name = customName.trim();
            }

            // Add prompt to request if provided
            if (prompt && prompt.trim() !== '') {
                requestBody.prompt = prompt.trim();
            }

            const options = await this.getFetchOptions('POST', requestBody);

            const response = await fetch(`${this.baseUrl}/process_suggested_list/`, options);

            // Check for auth errors
            if (this.handleAuthError(response)) {
                return false;
            }

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data.status || false;
        } catch (error) {
            console.error("Error processing suggested list:", error);
            return false;
        }
    }

    /**
     * Request a verification code for Telegram import
     * @param {string} phoneNumber - The phone number to send the code to
     * @returns {Promise<boolean>} - Whether the operation was successful
     */
    async requestCode(phoneNumber) {
        try {
            console.log(`Requesting verification code for ${phoneNumber}...`);

            const response = await fetch(`${this.baseUrl}/telegram/phone`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    phone_number: phoneNumber
                }),
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error(`❌ Failed to request code: HTTP ${response.status} - ${errorText}`);
                return false;
            }

            console.log("✅ Verification code requested successfully");
            return true;
        } catch (error) {
            console.error("❌ Failed to request verification code:", error);
            return false;
        }
    }

    /**
     * Verify a code for Telegram import and get available channels
     * @param {string} phoneNumber - The phone number
     * @param {string} code - The verification code
     * @param {string} listID - The list ID (optional, used for context)
     * @returns {Promise<Object>} - Object with success flag and channels array if successful
     */
    async verifyCode(phoneNumber, code, listID) {
        try {
            console.log(`Verifying code for ${phoneNumber}...`);

            const response = await fetch(`${this.baseUrl}/telegram/code`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    phone_number: phoneNumber,
                    code: code
                }),
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error(`❌ Failed to verify code: HTTP ${response.status} - ${errorText}`);
                return { success: false, channels: [] };
            }

            const data = await response.json();

            if (data.success) {
                console.log("✅ Code verified successfully, received channels:", data.channels.length);
                return { success: true, channels: data.channels };
            } else {
                console.error(`❌ API error: ${data.message || 'Unknown error'}`);
                return { success: false, channels: [] };
            }
        } catch (error) {
            console.error("❌ Failed to verify code:", error);
            return { success: false, channels: [] };
        }
    }

    /**
     * Select Telegram channels to import
     * @param {string} listID - The list ID to import channels to
     * @param {Array<string>} selectedChannels - Array of channel usernames to import
     * @returns {Promise<boolean>} - Whether the operation was successful
     */
    async selectChannels(listID, selectedChannels) {
        try {
            console.log(`Selecting ${selectedChannels.length} channels for list ${listID}...`);

            const body = {
                list_id: listID,
                selected_channels: selectedChannels
            };

            const options = await this.getFetchOptions('POST', body);
            const response = await fetch(`${this.baseUrl}/telegram/select-channels`, options);

            if (this.handleAuthError(response)) {
                return false;
            }

            if (!response.ok) {
                const errorText = await response.text();
                console.error(`❌ Failed to select channels: HTTP ${response.status} - ${errorText}`);
                return false;
            }

            console.log("✅ Channels selected successfully");
            return true;
        } catch (error) {
            console.error("❌ Failed to select channels:", error);
            return false;
        }
    }

    /**
     * Get the current Firebase authentication token
     * @returns {Promise<string|null>} The auth token or null if not available
     */
    /**
     * Get the current Firebase authentication token
     * @returns {Promise<string|null>} The auth token or null if not available
     */
    async getAuthToken() {
        try {
            // Try to get the auth instance from Firebase v9
            if (typeof window !== 'undefined') {
                try {
                    // Dynamically import Firebase auth
                    const { getAuth, onAuthStateChanged } = await import('firebase/auth');
                    const auth = getAuth();

                    // 1. Check if user is already available synchronously
                    if (auth && auth.currentUser) {
                        try {
                            const token = await auth.currentUser.getIdToken();
                            return token;
                        } catch (e) {
                            console.error("❌ getAuthToken: Error refreshing token from currentUser:", e);
                        }
                    }

                    // 2. If no user yet, wait a short moment for auth state to resolve
                    // This handles the race condition where auth is initializing
                    const tokenFromAuthState = await new Promise((resolve) => {
                        const unsubscribe = onAuthStateChanged(auth, async (user) => {
                            unsubscribe(); // Unsub immediately
                            if (user) {
                                try {
                                    const token = await user.getIdToken();
                                    resolve(token);
                                } catch (e) {
                                    console.error("Error getting token from resolved user:", e);
                                    resolve(null);
                                }
                            } else {
                                resolve(null);
                            }
                        });

                        // Timeout safety - don't wait too long
                        setTimeout(() => {
                            unsubscribe();
                            resolve(null);
                        }, 2500);
                    });

                    if (tokenFromAuthState) {
                        return tokenFromAuthState;
                    }

                } catch (importError) {
                    console.error("❌ Error importing Firebase auth:", importError);
                }
            }

            // 3. Last resort: try to use localStorage for a token
            if (typeof localStorage !== 'undefined') {
                console.log("⚠️ Attempting to fetch token from localStorage as fallback");
                const storedToken = localStorage.getItem('firebaseAuthToken');
                const tokenExpiry = localStorage.getItem('firebaseAuthTokenExpiry');

                if (storedToken && tokenExpiry) {
                    const expiryTime = parseInt(tokenExpiry, 10);
                    const currentTime = new Date().getTime();

                    // Check if token is still valid (not expired)
                    if (currentTime < expiryTime) {
                        console.log("⚠️ Using stored token from localStorage (valid)");
                        return storedToken;
                    } else {
                        console.log("⚠️ Stored token is expired, removing it");
                        localStorage.removeItem('firebaseAuthToken');
                        localStorage.removeItem('firebaseAuthTokenExpiry');
                    }
                }
            }

            console.error("❌ Firebase auth is not available and no valid fallback token!");
            return null;
        } catch (error) {
            console.error("❌ Error getting Firebase auth token:", error);
            return null;
        }
    }

    /**
     * Format date to YYYY-MM-DD HH:MM:SS format
     */
    formatDateForAPI(date) {
        if (!date) return null;

        // Check if date is already a string in the right format
        if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(date)) {
            return date;
        }

        // Otherwise create Date object and format it
        const d = new Date(date);

        // Format to YYYY-MM-DD HH:MM:SS
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
    }

    /**
     * Fetch message stats (relevant / irrelevant) for the authenticated listener
     * @param {number} windowMinutes - Time window in minutes (default 60, max 1440)
     * @returns {Promise<{relevant_count:number, irrelevant_count:number}|null>} Stats object or null on error
     */
    async getMessageStats(windowMinutes = 60) {
        try {
            const options = await this.getFetchOptions();
            const url = `${this.baseUrl}/stats/messages?window_minutes=${encodeURIComponent(windowMinutes)}`;
            const response = await fetch(url, options);

            if (this.handleAuthError(response)) {
                return null;
            }

            if (!response.ok) {
                console.error(`HTTP error fetching message stats! status: ${response.status}`);
                return null;
            }

            const data = await response.json();
            console.log('📊 Message stats response:', data);
            return {
                relevant_count: data.relevant_count || 0,
                irrelevant_count: data.irrelevant_count || 0
            };
        } catch (error) {
            console.error("Error fetching message stats:", error);
            return null;
        }
    }

    async generateReport(config) {
        const options = await this.getFetchOptions('POST', config);
        const response = await fetch(`${this.baseUrl}/reports/generate`, options);
        if (!response.ok) {
            const err = await response.text().catch(() => 'Unknown error');
            throw new Error(`Failed to generate report: ${err}`);
        }
        return response.blob();
    }

    async getReportTemplates() {
        const options = await this.getFetchOptions();
        const response = await fetch(`${this.baseUrl}/reports/templates`, options);
        if (!response.ok) return null;
        return response.json();
    }

    /**
     * Fetch brand insights dashboard data
     * @returns {Promise<Object|null>} Dashboard data or null on error
     */
    async fetchBrandDashboard(orgId = null, range = '7d') {
        try {
            const options = await this.getFetchOptions();
            let url = `${this.baseUrl}/insights/dashboard?range=${range}`;
            if (orgId) url += `&org_id=${orgId}`;
            const response = await fetch(url, options);

            if (this.handleAuthError(response)) return null;

            if (!response.ok) {
                console.error(`HTTP error fetching brand dashboard! status: ${response.status}`);
                return null;
            }

            return await response.json();
        } catch (error) {
            console.error("Error fetching brand dashboard:", error);
            return null;
        }
    }

    // Lazy-loaded dashboard tab sections: 'general' | 'topics' | 'competitive'.
    // Returns the section's slice of keys to merge into the dashboard data object.
    async fetchDashboardSection(section, orgId = null, range = '7d') {
        try {
            const options = await this.getFetchOptions();
            let url = `${this.baseUrl}/insights/dashboard/${section}?range=${range}`;
            if (orgId) url += `&org_id=${orgId}`;
            const response = await fetch(url, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) {
                console.error(`HTTP error fetching dashboard ${section}! status: ${response.status}`);
                return null;
            }
            return await response.json();
        } catch (error) {
            console.error(`Error fetching dashboard ${section}:`, error);
            return null;
        }
    }

    async fetchWordPosts(word, limit = 20, offset = 0, orgId = null, range = '7d') {
        try {
            const options = await this.getFetchOptions();
            let url = `${this.baseUrl}/insights/word-posts?word=${encodeURIComponent(word)}&limit=${limit}&offset=${offset}&range=${range}`;
            if (orgId) url += `&org_id=${orgId}`;
            const response = await fetch(url, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error fetching word posts:", error);
            return null;
        }
    }

    async fetchWordCloud(orgId = null, range = '7d', limit = 80) {
        try {
            const options = await this.getFetchOptions();
            let url = `${this.baseUrl}/insights/word-cloud?range=${range}&limit=${limit}`;
            if (orgId) url += `&org_id=${orgId}`;
            const response = await fetch(url, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error fetching word cloud:", error);
            return null;
        }
    }

    async fetchExtremePosts(type = 'negative', limit = 10, offset = 0, orgId = null) {
        try {
            const options = await this.getFetchOptions();
            let url = `${this.baseUrl}/insights/extreme-posts?type=${type}&limit=${limit}&offset=${offset}`;
            if (orgId) url += `&org_id=${orgId}`;
            const response = await fetch(url, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await this.parseListWithTotal(response);
        } catch (error) {
            console.error("Error fetching extreme posts:", error);
            return null;
        }
    }

    async fetchEngagementPosts(sort = 'views', limit = 20, offset = 0, orgId = null, range = '7d') {
        try {
            const options = await this.getFetchOptions();
            let url = `${this.baseUrl}/insights/engagement-posts?sort=${sort}&limit=${limit}&offset=${offset}&range=${range}`;
            if (orgId) url += `&org_id=${orgId}`;
            const response = await fetch(url, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await this.parseListWithTotal(response);
        } catch (error) {
            console.error("Error fetching engagement posts:", error);
            return null;
        }
    }

    async fetchTagPosts(tag, limit = 20, offset = 0, orgId = null, range = '7d') {
        try {
            const options = await this.getFetchOptions();
            let url = `${this.baseUrl}/insights/tag-posts?tag=${encodeURIComponent(tag)}&limit=${limit}&offset=${offset}&range=${range}`;
            if (orgId) url += `&org_id=${orgId}`;
            const response = await fetch(url, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error fetching tag posts:", error);
            return null;
        }
    }

    async fetchTagTimeline(tag, range = '7d', orgId = null) {
        try {
            const options = await this.getFetchOptions();
            let url = `${this.baseUrl}/insights/tag-timeline?tag=${encodeURIComponent(tag)}&range=${range}`;
            if (orgId) url += `&org_id=${orgId}`;
            const response = await fetch(url, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error fetching tag timeline:", error);
            return null;
        }
    }

    async fetchHashtagPosts(tag, limit = 20, offset = 0, orgId = null, range = '7d') {
        try {
            const options = await this.getFetchOptions();
            let url = `${this.baseUrl}/insights/hashtag-posts?tag=${encodeURIComponent(tag)}&limit=${limit}&offset=${offset}&range=${range}`;
            if (orgId) url += `&org_id=${orgId}`;
            const response = await fetch(url, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error fetching hashtag posts:", error);
            return null;
        }
    }

    async fetchEntityPosts(entityName, category, limit = 20, offset = 0, orgId = null, range = '7d') {
        try {
            const options = await this.getFetchOptions();
            let url = `${this.baseUrl}/insights/entity-posts?name=${encodeURIComponent(entityName)}&category=${category}&limit=${limit}&offset=${offset}&range=${range}`;
            if (orgId) url += `&org_id=${orgId}`;
            const response = await fetch(url, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error fetching entity posts:", error);
            return null;
        }
    }

    async fetchEmotionPosts(emotion, limit = 20, offset = 0, orgId = null, listId = null, range = '7d') {
        try {
            const options = await this.getFetchOptions();
            let url = `${this.baseUrl}/insights/emotion-posts?emotion=${encodeURIComponent(emotion)}&limit=${limit}&offset=${offset}&range=${range}`;
            if (orgId) url += `&org_id=${orgId}`;
            if (listId) url += `&list_id=${listId}`;
            const response = await fetch(url, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error fetching emotion posts:", error);
            return null;
        }
    }

    async fetchPlatformPosts(platform, limit = 20, offset = 0, orgId = null, listId = null, range = '7d') {
        try {
            const options = await this.getFetchOptions();
            let url = `${this.baseUrl}/insights/platform-posts?platform=${encodeURIComponent(platform)}&limit=${limit}&offset=${offset}&range=${range}`;
            if (orgId) url += `&org_id=${orgId}`;
            if (listId) url += `&list_id=${listId}`;
            const response = await fetch(url, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await this.parseListWithTotal(response);
        } catch (error) {
            console.error("Error fetching platform posts:", error);
            return null;
        }
    }

    async fetchSentimentPosts(sentiment, limit = 20, offset = 0, orgId = null, range = '7d') {
        try {
            const options = await this.getFetchOptions();
            let url = `${this.baseUrl}/insights/sentiment-posts?sentiment=${encodeURIComponent(sentiment)}&limit=${limit}&offset=${offset}&range=${range}`;
            if (orgId) url += `&org_id=${orgId}`;
            const response = await fetch(url, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await this.parseListWithTotal(response);
        } catch (error) {
            console.error("Error fetching sentiment posts:", error);
            return null;
        }
    }

    async fetchAuthorPosts(username, source, limit = 20, offset = 0, orgId = null, range = '7d') {
        try {
            const options = await this.getFetchOptions();
            let url = `${this.baseUrl}/insights/author-posts?username=${encodeURIComponent(username)}&source=${encodeURIComponent(source)}&limit=${limit}&offset=${offset}&range=${range}`;
            if (orgId) url += `&org_id=${orgId}`;
            const response = await fetch(url, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await this.parseListWithTotal(response);
        } catch (error) {
            console.error("Error fetching author posts:", error);
            return null;
        }
    }

    async fetchCompetitorPosts(competitor, limit = 20, offset = 0, orgId = null, range = '7d', competitorId = null) {
        try {
            const options = await this.getFetchOptions();
            let url = `${this.baseUrl}/insights/competitor-posts?limit=${limit}&offset=${offset}&range=${range}`;
            if (competitorId) {
                url += `&competitor_id=${encodeURIComponent(competitorId)}`;
            } else if (competitor) {
                url += `&competitor=${encodeURIComponent(competitor)}`;
            }
            if (orgId) url += `&org_id=${orgId}`;
            const response = await fetch(url, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error fetching competitor posts:", error);
            return null;
        }
    }

    // ========================================================================
    // Action Queue
    // ========================================================================

    async fetchActionQueue(limit = 7, category = null, orgId = null) {
        try {
            const options = await this.getFetchOptions();
            let url = `${this.baseUrl}/actions/queue?limit=${limit}&status=open,in_progress,acted`;
            if (category && category !== 'all') url += `&category=${category}`;
            if (orgId) url += `&org_id=${orgId}`;
            const response = await fetch(url, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error fetching action queue:", error);
            return null;
        }
    }

    async updateActionStatus(actionId, status, effectiveness = null, outcomeNotes = null, orgId = null, actionTaken = null, executionDetails = null) {
        try {
            const body = { status };
            if (effectiveness !== null) body.effectiveness = effectiveness;
            if (outcomeNotes) body.outcome_notes = outcomeNotes;
            if (orgId) body.org_id = orgId;
            if (actionTaken) body.action_taken = actionTaken;
            if (executionDetails) body.execution_details = executionDetails;
            const options = await this.getFetchOptions('PATCH', body);
            const response = await fetch(`${this.baseUrl}/actions/${actionId}/status`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error updating action status:", error);
            return null;
        }
    }

    async updateWorkStatus(actionId, workStatus, orgId) {
        try {
            const options = await this.getFetchOptions('PATCH', { work_status: workStatus, org_id: orgId });
            const response = await fetch(`${this.baseUrl}/actions/${actionId}/work-status`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error updating work status:", error);
            return null;
        }
    }

    async claimAction(actionId, orgId) {
        try {
            const options = await this.getFetchOptions('POST');
            const response = await fetch(`${this.baseUrl}/actions/${actionId}/claim?org_id=${orgId}`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error claiming action:", error);
            return null;
        }
    }

    async assignAction(actionId, orgId, assignedToListenerId) {
        try {
            const options = await this.getFetchOptions('POST', { org_id: orgId, assigned_to: assignedToListenerId });
            const response = await fetch(`${this.baseUrl}/actions/${actionId}/assign`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error assigning action:", error);
            return null;
        }
    }

    async unassignAction(actionId, targetListenerId, orgId) {
        try {
            const options = await this.getFetchOptions('DELETE');
            const response = await fetch(`${this.baseUrl}/actions/${actionId}/assignees/${targetListenerId}?org_id=${orgId}`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error unassigning action:", error);
            return null;
        }
    }

    async fetchPastCases() {
        try {
            const options = await this.getFetchOptions();
            const response = await fetch(`${this.baseUrl}/reactions/past-cases`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error fetching past cases:", error);
            return null;
        }
    }

    async createPastCase(title, situationDescription, actionTaken, executionDetails, effectiveness, outcomeNotes, platform) {
        try {
            AnalyticsService.logActivity('create_past_case', { title, platform });
            const options = await this.getFetchOptions('POST', {
                title,
                situation_description: situationDescription || '',
                action_taken: actionTaken,
                execution_details: executionDetails || '',
                effectiveness,
                outcome_notes: outcomeNotes || '',
                platform: platform || 'all'
            });
            const response = await fetch(`${this.baseUrl}/reactions/past-cases`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error creating past case:", error);
            return null;
        }
    }

    async deletePastCase(caseId) {
        try {
            const options = await this.getFetchOptions('DELETE');
            const response = await fetch(`${this.baseUrl}/reactions/past-cases/${caseId}`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error deleting past case:", error);
            return null;
        }
    }

    async fetchNarrativePosts(narrativeId, limit = 20, offset = 0) {
        try {
            const options = await this.getFetchOptions();
            const url = `${this.baseUrl}/actions/narrative-posts/${narrativeId}?limit=${limit}&offset=${offset}`;
            const response = await fetch(url, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error fetching narrative posts:", error);
            return null;
        }
    }

    async refreshActionQueue(orgId = null) {
        try {
            const options = await this.getFetchOptions('POST');
            const url = orgId
                ? `${this.baseUrl}/actions/refresh?org_id=${encodeURIComponent(orgId)}`
                : `${this.baseUrl}/actions/refresh`;
            const response = await fetch(url, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error refreshing action queue:", error);
            return null;
        }
    }

    // ========================================================================
    // Tracking & Outcomes
    // ========================================================================

    async fetchTrackers(orgId, status = 'active', trackerType = null, limit = 20, offset = 0) {
        try {
            const options = await this.getFetchOptions();
            let url = `${this.baseUrl}/trackers?org_id=${encodeURIComponent(orgId)}&status=${encodeURIComponent(status)}&limit=${limit}&offset=${offset}`;
            if (trackerType) url += `&tracker_type=${encodeURIComponent(trackerType)}`;
            const response = await fetch(url, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error fetching trackers:", error);
            return null;
        }
    }

    async fetchTrackerSummary(orgId) {
        try {
            const options = await this.getFetchOptions();
            const response = await fetch(`${this.baseUrl}/trackers/summary?org_id=${encodeURIComponent(orgId)}`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error fetching tracker summary:", error);
            return null;
        }
    }

    async fetchTrackerDetail(trackerId) {
        try {
            const options = await this.getFetchOptions();
            const response = await fetch(`${this.baseUrl}/trackers/${encodeURIComponent(trackerId)}`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error fetching tracker detail:", error);
            return null;
        }
    }

    async createTracker(body) {
        try {
            AnalyticsService.logActivity('create_tracker', { type: body?.tracker_type });
            const options = await this.getFetchOptions('POST', body);
            const response = await fetch(`${this.baseUrl}/trackers`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error creating tracker:", error);
            return null;
        }
    }

    async refreshTracker(trackerId) {
        try {
            const options = await this.getFetchOptions('POST');
            const response = await fetch(`${this.baseUrl}/trackers/${encodeURIComponent(trackerId)}/refresh`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                return { error: err.detail || 'Refresh failed' };
            }
            return await response.json();
        } catch (error) {
            console.error("Error refreshing tracker:", error);
            return null;
        }
    }

    async enrichTracker(trackerId) {
        try {
            const language = await this.getListenerLanguage();
            const options = await this.getFetchOptions('POST');
            const response = await fetch(`${this.baseUrl}/trackers/${encodeURIComponent(trackerId)}/enrich?language=${language}`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                return { error: err.detail || 'Enrichment failed' };
            }
            return await response.json();
        } catch (error) {
            console.error("Error enriching tracker:", error);
            return null;
        }
    }

    async updateTracker(trackerId, body) {
        try {
            const options = await this.getFetchOptions('PATCH', body);
            const response = await fetch(`${this.baseUrl}/trackers/${encodeURIComponent(trackerId)}`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error updating tracker:", error);
            return null;
        }
    }

    async trackNarrative(narrativeId, orgId) {
        try {
            AnalyticsService.logActivity('track_narrative', { narrative_id: narrativeId });
            const options = await this.getFetchOptions('POST');
            const orgParam = orgId ? `?org_id=${encodeURIComponent(orgId)}` : '';
            const response = await fetch(`${this.baseUrl}/trackers/narrative/${encodeURIComponent(narrativeId)}${orgParam}`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error tracking narrative:", error);
            return null;
        }
    }

    // ========================================================================
    // Post Action Advisor
    // ========================================================================

    async analyzePostAction(sourceType, sourcePostId) {
        try {
            AnalyticsService.logActivity('analyze_post', { source_type: sourceType });
            const options = await this.getFetchOptions('POST', {
                source_type: sourceType,
                source_post_id: sourcePostId,
            });
            const response = await fetch(`${this.baseUrl}/post-actions/analyze`, options);
            if (this.handleAuthError(response)) return { success: false, error_message: 'Auth error' };
            if (!response.ok) {
                const err = await response.json();
                return { success: false, error_message: err.detail || 'Analysis failed' };
            }
            return { success: true, ...(await response.json()) };
        } catch (error) {
            console.error("Error analyzing post action:", error);
            return { success: false, error_message: error.message };
        }
    }

    async analyzePostActionBatch(posts) {
        try {
            const options = await this.getFetchOptions('POST', { posts });
            const response = await fetch(`${this.baseUrl}/post-actions/analyze-batch`, options);
            if (this.handleAuthError(response)) return { success: false, error_message: 'Auth error' };
            if (!response.ok) {
                const err = await response.json();
                return { success: false, error_message: err.detail || 'Batch analysis failed' };
            }
            return { success: true, ...(await response.json()) };
        } catch (error) {
            console.error("Error in batch analyze:", error);
            return { success: false, error_message: error.message };
        }
    }

    async updatePostActionStatus(actionId, status) {
        try {
            const options = await this.getFetchOptions('PATCH', { status });
            const response = await fetch(`${this.baseUrl}/post-actions/${actionId}/status`, options);
            if (this.handleAuthError(response)) return { success: false, error_message: 'Auth error' };
            if (!response.ok) {
                const err = await response.json();
                return { success: false, error_message: err.detail || 'Update failed' };
            }
            return { success: true, ...(await response.json()) };
        } catch (error) {
            console.error("Error updating action status:", error);
            return { success: false, error_message: error.message };
        }
    }

    async fetchPostActions(status = null) {
        try {
            const url = status
                ? `${this.baseUrl}/post-actions?status=${status}`
                : `${this.baseUrl}/post-actions`;
            const options = await this.getFetchOptions();
            const response = await fetch(url, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error fetching post actions:", error);
            return null;
        }
    }

    /**
     * Fetch enriched strategic intelligence for a narrative
     * @param {string} narrativeId
     * @param {boolean} forceRefresh
     * @param {string} language
     */
    // =====================================================================
    // CAMPAIGNS API
    // =====================================================================

    async fetchCampaigns(status = null, orgId = null) {
        try {
            const params = new URLSearchParams();
            if (status) params.set('status', status);
            if (orgId) params.set('org_id', orgId);
            const query = params.toString();
            const url = `${this.baseUrl}/campaigns/${query ? '?' + query : ''}`;
            const options = await this.getFetchOptions();
            const response = await fetch(url, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error fetching campaigns:", error);
            return null;
        }
    }

    async fetchCampaignPreview(orgId = null) {
        try {
            const url = orgId
                ? `${this.baseUrl}/campaigns/preview?org_id=${orgId}`
                : `${this.baseUrl}/campaigns/preview`;
            const options = await this.getFetchOptions();
            const response = await fetch(url, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error fetching campaign preview:", error);
            return null;
        }
    }

    async fetchCampaignDetail(campaignId, orgId = null) {
        try {
            const url = orgId
                ? `${this.baseUrl}/campaigns/${campaignId}?org_id=${orgId}`
                : `${this.baseUrl}/campaigns/${campaignId}`;
            const options = await this.getFetchOptions();
            const response = await fetch(url, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error fetching campaign detail:", error);
            return null;
        }
    }

    async createCampaign(name, description = null, visibility = 'org', orgId = null) {
        try {
            AnalyticsService.logActivity('create_campaign', { name });
            const options = await this.getFetchOptions('POST', { name, description, visibility, org_id: orgId });
            const response = await fetch(`${this.baseUrl}/campaigns/`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error creating campaign:", error);
            return null;
        }
    }

    async updateCampaign(campaignId, updates, orgId = null) {
        try {
            const url = orgId
                ? `${this.baseUrl}/campaigns/${campaignId}?org_id=${orgId}`
                : `${this.baseUrl}/campaigns/${campaignId}`;
            const options = await this.getFetchOptions('PATCH', updates);
            const response = await fetch(url, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error updating campaign:", error);
            return null;
        }
    }

    async deleteCampaign(campaignId, orgId = null) {
        try {
            const url = orgId
                ? `${this.baseUrl}/campaigns/${campaignId}?org_id=${orgId}`
                : `${this.baseUrl}/campaigns/${campaignId}`;
            const options = await this.getFetchOptions('DELETE');
            const response = await fetch(url, options);
            if (this.handleAuthError(response)) return null;
            return response.ok;
        } catch (error) {
            console.error("Error deleting campaign:", error);
            return false;
        }
    }

    async addCampaignAccounts(campaignId, accounts) {
        // accounts: [{ platform, username }, ...]
        try {
            const options = await this.getFetchOptions('POST', { accounts });
            const response = await fetch(`${this.baseUrl}/campaigns/${campaignId}/accounts`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error adding campaign accounts:", error);
            return null;
        }
    }

    async removeCampaignAccount(campaignId, accountId) {
        try {
            const options = await this.getFetchOptions('DELETE');
            const response = await fetch(`${this.baseUrl}/campaigns/${campaignId}/accounts/${accountId}`, options);
            if (this.handleAuthError(response)) return null;
            return response.ok;
        } catch (error) {
            console.error("Error removing campaign account:", error);
            return false;
        }
    }

    async updateCampaignAccount(campaignId, accountId, updates) {
        try {
            const options = await this.getFetchOptions('PATCH', updates);
            const response = await fetch(`${this.baseUrl}/campaigns/${campaignId}/accounts/${accountId}`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error updating campaign account:", error);
            return null;
        }
    }

    async previewAccountPosts(campaignId, accountId) {
        try {
            const options = await this.getFetchOptions();
            const response = await fetch(`${this.baseUrl}/campaigns/${campaignId}/accounts/${accountId}/preview-posts`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error previewing account posts:", error);
            return null;
        }
    }

    async selectAccountPosts(campaignId, accountId, postIds, autoMonitor = false) {
        try {
            const options = await this.getFetchOptions('POST', { post_ids: postIds, auto_monitor: autoMonitor });
            const response = await fetch(`${this.baseUrl}/campaigns/${campaignId}/accounts/${accountId}/select-posts`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error selecting account posts:", error);
            return null;
        }
    }

    async addCampaignPost(campaignId, url) {
        try {
            const options = await this.getFetchOptions('POST', { url });
            const response = await fetch(`${this.baseUrl}/campaigns/${campaignId}/posts`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error adding campaign post:", error);
            return null;
        }
    }

    async fetchPostTimeline(campaignId, postId) {
        try {
            const options = await this.getFetchOptions();
            const response = await fetch(`${this.baseUrl}/campaigns/${campaignId}/posts/${postId}/timeline`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error fetching post timeline:", error);
            return null;
        }
    }

    async removeCampaignPost(campaignId, postId) {
        try {
            const options = await this.getFetchOptions('DELETE');
            const response = await fetch(`${this.baseUrl}/campaigns/${campaignId}/posts/${postId}`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error removing campaign post:", error);
            return null;
        }
    }

    async fetchCampaignDashboard(campaignId) {
        try {
            const options = await this.getFetchOptions();
            const response = await fetch(`${this.baseUrl}/campaigns/${campaignId}/dashboard`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error fetching campaign dashboard:", error);
            return null;
        }
    }

    async fetchPostComments(campaignId, postId, sort = 'most_positive', limit = 20, offset = 0) {
        try {
            const options = await this.getFetchOptions();
            const response = await fetch(
                `${this.baseUrl}/campaigns/${campaignId}/posts/${postId}/comments?sort=${sort}&limit=${limit}&offset=${offset}`,
                options
            );
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error fetching post comments:", error);
            return null;
        }
    }

    async fetchPostCommentsOverview(campaignId, postId) {
        try {
            const options = await this.getFetchOptions();
            const response = await fetch(
                `${this.baseUrl}/campaigns/${campaignId}/posts/${postId}/comments/overview`,
                options
            );
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error fetching post comments overview:", error);
            return null;
        }
    }

    async fetchCampaignCommentsOverview(campaignId) {
        try {
            const options = await this.getFetchOptions();
            const response = await fetch(
                `${this.baseUrl}/campaigns/${campaignId}/comments/overview`,
                options
            );
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error fetching campaign comments overview:", error);
            return null;
        }
    }

    async compareCampaigns(ids) {
        try {
            const qs = ids.join(',');
            const language = await this.getListenerLanguage();
            const options = await this.getFetchOptions();
            const response = await fetch(`${this.baseUrl}/campaigns/compare?ids=${qs}&language=${language}`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error comparing campaigns:", error);
            return null;
        }
    }

    async addComparisonWatch(campaignIds, campaignNames, note) {
        try {
            const options = await this.getFetchOptions('POST', {
                campaign_ids: campaignIds,
                campaign_names: campaignNames,
                watch_note: note || null,
            });
            const response = await fetch(`${this.baseUrl}/campaigns/watch-comparisons`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error adding comparison watch:", error);
            return null;
        }
    }

    async getComparisonWatches() {
        try {
            const options = await this.getFetchOptions();
            const response = await fetch(`${this.baseUrl}/campaigns/watch-comparisons`, options);
            if (this.handleAuthError(response)) return [];
            if (!response.ok) return [];
            return await response.json();
        } catch (error) {
            console.error("Error fetching comparison watches:", error);
            return [];
        }
    }

    async removeComparisonWatch(watchId) {
        try {
            const options = await this.getFetchOptions('DELETE');
            const response = await fetch(`${this.baseUrl}/campaigns/watch-comparisons/${watchId}`, options);
            if (this.handleAuthError(response)) return false;
            return response.ok;
        } catch (error) {
            console.error("Error removing comparison watch:", error);
            return false;
        }
    }

    async fetchNarrativeEnrichment(narrativeId, forceRefresh = false, language = null) {
        try {
            const effectiveLanguage = language || await this.getListenerLanguage();
            const options = await this.getFetchOptions();
            const url = `${this.baseUrl}/narratives/${narrativeId}/enrichment?force_refresh=${forceRefresh}&language=${effectiveLanguage}`;

            console.log(`🔍 Fetching enrichment for ${narrativeId}...`);
            const response = await fetch(url, options);

            if (this.handleAuthError(response)) {
                return null;
            }

            if (!response.ok) {
                console.error(`Error fetching narrative enrichment: status ${response.status}`);
                return null;
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error("Error fetching narrative enrichment:", error);
            return null;
        }
    }

    /**
     * Fetch all active RSS feeds available in the registry
     */
    async fetchRssFeeds() {
        try {
            const options = await this.getFetchOptions();
            const response = await fetch(`${this.baseUrl}/rss/feeds`, options);
            if (this.handleAuthError(response)) return [];
            if (!response.ok) {
                console.error(`Error fetching RSS feeds: HTTP ${response.status}`);
                return [];
            }
            return await response.json();
        } catch (error) {
            console.error("Error fetching RSS feeds:", error);
            return [];
        }
    }

    /**
     * Discover and register a new RSS feed from any URL (runs LangGraph agent)
     * @param {string} url - Any URL: website homepage or direct RSS/Atom feed URL
     * @returns {Promise<{success: boolean, feed: Object}|null>}
     */
    async discoverRssFeeds(url) {
        try {
            const options = await this.getFetchOptions('POST', { url });
            const response = await fetch(`${this.baseUrl}/rss/discover`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                console.error(`Error discovering RSS feeds: HTTP ${response.status}`, err);
                return null;
            }
            return await response.json(); // { candidates: [...] }
        } catch (error) {
            console.error("Error discovering RSS feeds:", error);
            return null;
        }
    }

    async addRssFeed(url) {
        try {
            AnalyticsService.logActivity('add_rss_feed', { url });
            const options = await this.getFetchOptions('POST', { url });
            const response = await fetch(`${this.baseUrl}/rss/add-feed`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) {
                const err = await response.json().catch(() => ({}));
                console.error(`Error adding RSS feed: HTTP ${response.status}`, err);
                return null;
            }
            return await response.json();
        } catch (error) {
            console.error("Error adding RSS feed:", error);
            return null;
        }
    }

    /**
     * Follow an RSS feed for a list
     * @param {string} feedName - Feed slug (e.g. "bbc-news")
     * @param {string} listName - Name of the list
     */
    async followRssFeed(feedName, listName) {
        try {
            const options = await this.getFetchOptions('POST', { feed_name: feedName, list_name: listName });
            const response = await fetch(`${this.baseUrl}/rss/follow`, options);
            if (this.handleAuthError(response)) return false;
            if (!response.ok) {
                console.error(`Error following RSS feed: HTTP ${response.status}`);
                return false;
            }
            return true;
        } catch (error) {
            console.error("Error following RSS feed:", error);
            return false;
        }
    }

    /**
     * Get RSS feeds followed for a specific list
     * @param {string} listID - UUID of the list
     */
    async getRssFollows(listID) {
        try {
            const options = await this.getFetchOptions();
            const response = await fetch(`${this.baseUrl}/rss/follows/${listID}`, options);
            if (this.handleAuthError(response)) return [];
            if (!response.ok) {
                console.error(`Error fetching RSS follows: HTTP ${response.status}`);
                return [];
            }
            return await response.json();
        } catch (error) {
            console.error("Error fetching RSS follows:", error);
            return [];
        }
    }

    /**
     * Unfollow an RSS feed for a list
     * @param {string} listID - UUID of the list
     * @param {string} feedName - Feed slug to unfollow
     */
    async deleteRssFeed(listID, feedName) {
        try {
            const options = await this.getFetchOptions('DELETE', { list_id: listID, feed_name: feedName });
            const response = await fetch(`${this.baseUrl}/rss/follows/delete`, options);
            if (this.handleAuthError(response)) return false;
            if (!response.ok) {
                console.error(`Error unfollowing RSS feed: HTTP ${response.status}`);
                return false;
            }
            return true;
        } catch (error) {
            console.error("Error unfollowing RSS feed:", error);
            return false;
        }
    }

    async fetchRelevantRssItemsFromList(listID, since = null, limit = 20, offset = 0, search = null, sortBy = 'created_at') {
        try {
            let url = `${this.baseUrl}/rss/${listID}?limit=${limit}&offset=${offset}`;
            if (search && String(search).trim() !== '') {
                url += `&search=${encodeURIComponent(search.trim())}`;
            }
            if (sortBy) {
                url += `&sort_by=${encodeURIComponent(sortBy)}`;
            }
            if (since) {
                const formattedDate = this.formatDateForAPI(since);
                url += `&since=${encodeURIComponent(formattedDate)}`;
            }
            if (offset === 0) {
                url += `&_=${new Date().getTime()}`;
            }
            const options = await this.getFetchOptions();
            const response = await fetch(url, options);
            if (this.handleAuthError(response)) return [];
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            return await this.parseListWithTotal(response);
        } catch (error) {
            console.error("Error fetching RSS items from list:", error);
            return [];
        }
    }

    async fetchAllRssItemsForListener(since = null, limit = 20, offset = 0, search = null, sortBy = 'created_at', filters = {}) {
        try {
            let url = `${this.baseUrl}/rss/all?limit=${limit}&offset=${offset}`;
            if (search && String(search).trim() !== '') {
                url += `&search=${encodeURIComponent(search.trim())}`;
            }
            if (sortBy) {
                url += `&sort_by=${encodeURIComponent(sortBy)}`;
            }
            if (since) {
                const formattedDate = this.formatDateForAPI(since);
                url += `&since=${encodeURIComponent(formattedDate)}`;
            }
            if (filters.emotion) url += `&emotion=${encodeURIComponent(filters.emotion)}`;
            if (filters.sentiment_min != null) url += `&sentiment_min=${filters.sentiment_min}`;
            if (filters.sentiment_max != null) url += `&sentiment_max=${filters.sentiment_max}`;
            if (filters.list_name) url += `&list_name=${encodeURIComponent(filters.list_name)}`;
            if (filters.list_role) url += `&list_role=${encodeURIComponent(filters.list_role)}`;
            if (filters.org_id) url += `&org_id=${encodeURIComponent(filters.org_id)}`;
            if (offset === 0) {
                url += `&_=${new Date().getTime()}`;
            }
            const options = await this.getFetchOptions();
            const response = await fetch(url, options);
            if (this.handleAuthError(response)) return [];
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            return await this.parseListWithTotal(response);
        } catch (error) {
            console.error("Error fetching all RSS items:", error);
            return [];
        }
    }

    // ========================================================================
    // TV (television clips)
    // ========================================================================

    async fetchRelevantTvSegmentsFromList(listID, since = null, limit = 20, offset = 0, search = null, sortBy = 'created_at') {
        try {
            let url = `${this.baseUrl}/tv/list/${listID}?limit=${limit}&offset=${offset}`;
            if (search && String(search).trim() !== '') {
                url += `&search=${encodeURIComponent(search.trim())}`;
            }
            if (sortBy) {
                url += `&sort_by=${encodeURIComponent(sortBy)}`;
            }
            if (since) {
                const formattedDate = this.formatDateForAPI(since);
                url += `&since=${encodeURIComponent(formattedDate)}`;
            }
            if (offset === 0) {
                url += `&_=${new Date().getTime()}`;
            }
            const options = await this.getFetchOptions();
            const response = await fetch(url, options);
            if (this.handleAuthError(response)) return [];
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            return await this.parseListWithTotal(response);
        } catch (error) {
            console.error("Error fetching TV segments from list:", error);
            return [];
        }
    }

    async fetchAllTvSegmentsForListener(since = null, limit = 20, offset = 0, search = null, sortBy = 'created_at', filters = {}) {
        try {
            let url = `${this.baseUrl}/tv/all?limit=${limit}&offset=${offset}`;
            if (search && String(search).trim() !== '') {
                url += `&search=${encodeURIComponent(search.trim())}`;
            }
            if (sortBy) {
                url += `&sort_by=${encodeURIComponent(sortBy)}`;
            }
            if (since) {
                const formattedDate = this.formatDateForAPI(since);
                url += `&since=${encodeURIComponent(formattedDate)}`;
            }
            if (filters.emotion) url += `&emotion=${encodeURIComponent(filters.emotion)}`;
            if (filters.sentiment_min != null) url += `&sentiment_min=${filters.sentiment_min}`;
            if (filters.sentiment_max != null) url += `&sentiment_max=${filters.sentiment_max}`;
            if (filters.list_name) url += `&list_name=${encodeURIComponent(filters.list_name)}`;
            if (filters.list_role) url += `&list_role=${encodeURIComponent(filters.list_role)}`;
            if (filters.org_id) url += `&org_id=${encodeURIComponent(filters.org_id)}`;
            if (offset === 0) {
                url += `&_=${new Date().getTime()}`;
            }
            const options = await this.getFetchOptions();
            const response = await fetch(url, options);
            if (this.handleAuthError(response)) return [];
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            return await this.parseListWithTotal(response);
        } catch (error) {
            console.error("Error fetching all TV segments:", error);
            return [];
        }
    }

    // ========================================================================
    // Webz.io (News Articles)
    // ========================================================================

    async fetchRelevantWebzArticlesFromList(listID, since = null, limit = 20, offset = 0, search = null, sortBy = 'created_at') {
        try {
            const options = await this.getFetchOptions();
            let url = `${this.baseUrl}/webz/${listID}?limit=${limit}&offset=${offset}`;
            if (search) url += `&search=${encodeURIComponent(search)}`;
            if (sortBy) url += `&sort_by=${sortBy}`;
            if (since) url += `&since=${since}`;
            const response = await fetch(url, options);
            if (this.handleAuthError(response)) return [];
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            return await this.parseListWithTotal(response);
        } catch (error) {
            console.error("Error fetching webz articles for list:", error);
            return [];
        }
    }

    async fetchAllWebzArticlesForListener(since = null, limit = 20, offset = 0, search = null, sortBy = 'created_at', filters = {}) {
        try {
            const options = await this.getFetchOptions();
            let url = `${this.baseUrl}/webz/all?limit=${limit}&offset=${offset}`;
            if (search) url += `&search=${encodeURIComponent(search)}`;
            if (sortBy) url += `&sort_by=${sortBy}`;
            if (since) url += `&since=${since}`;
            if (filters.emotion) url += `&emotion=${encodeURIComponent(filters.emotion)}`;
            if (filters.sentiment_min != null) url += `&sentiment_min=${filters.sentiment_min}`;
            if (filters.sentiment_max != null) url += `&sentiment_max=${filters.sentiment_max}`;
            if (filters.list_name) url += `&list_name=${encodeURIComponent(filters.list_name)}`;
            if (filters.list_role) url += `&list_role=${encodeURIComponent(filters.list_role)}`;
            if (filters.org_id) url += `&org_id=${encodeURIComponent(filters.org_id)}`;
            const response = await fetch(url, options);
            if (this.handleAuthError(response)) return [];
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            return await this.parseListWithTotal(response);
        } catch (error) {
            console.error("Error fetching all webz articles:", error);
            return [];
        }
    }

    /**
     * Fetch reader comments harvested for a news article.
     * @param {string} articleUrl - The article URL (post.url for webz/news posts)
     * @param {{orgId?: string, sort?: 'top'|'newest', limit?: number, offset?: number}} opts
     * @returns {Promise<{comments: Array, total: number, has_more: boolean}|null>}
     */
    async fetchArticleComments(articleUrl, { orgId = null, sort = 'top', limit = 200, offset = 0 } = {}) {
        if (!articleUrl) return null;
        try {
            const options = await this.getFetchOptions();
            let url = `${this.baseUrl}/webz/comments?article_url=${encodeURIComponent(articleUrl)}&sort=${sort}&limit=${limit}&offset=${offset}`;
            if (orgId) url += `&org_id=${encodeURIComponent(orgId)}`;
            const response = await fetch(url, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error fetching article comments:", error);
            return null;
        }
    }

    // ========================================================================
    // Signal Feed (V3 Category-First)
    // ========================================================================

    async fetchSignalCategories(orgId = null) {
        try {
            const options = await this.getFetchOptions();
            let url = `${this.baseUrl}/actions/categories`;
            if (orgId) url += `?org_id=${orgId}`;
            const response = await fetch(url, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error fetching signal categories:", error);
            return null;
        }
    }

    async fetchSignalFeed(orgId, category, { sort = 'priority', limit = 20, offset = 0, status = null } = {}) {
        try {
            const options = await this.getFetchOptions();
            let url = `${this.baseUrl}/actions/feed/${category}?sort=${sort}&limit=${limit}&offset=${offset}`;
            if (orgId) url += `&org_id=${orgId}`;
            if (status) url += `&status=${status}`;
            const response = await fetch(url, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error fetching signal feed:", error);
            return null;
        }
    }

    async updateSignalFeedStatus(itemId, status, orgId = null) {
        try {
            const body = { status };
            if (orgId) body.org_id = orgId;
            const options = await this.getFetchOptions('PATCH', body);
            const response = await fetch(`${this.baseUrl}/actions/feed/${itemId}/status`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error updating signal feed status:", error);
            return null;
        }
    }

    async escalateSignalFeedItem(itemId, orgId = null) {
        try {
            const body = {};
            if (orgId) body.org_id = orgId;
            const options = await this.getFetchOptions('POST', body);
            const response = await fetch(`${this.baseUrl}/actions/feed/${itemId}/escalate`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error escalating signal feed item:", error);
            return null;
        }
    }

    async recordSignalFeedAction(itemId, actionTaken, orgId, notes = null) {
        try {
            const body = { org_id: orgId, action_taken: actionTaken };
            if (notes) body.notes = notes;
            const options = await this.getFetchOptions('POST', body);
            const response = await fetch(`${this.baseUrl}/actions/feed/${itemId}/act`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error recording signal feed action:", error);
            return null;
        }
    }

    async batchUpdateSignalFeed(itemIds, status, orgId = null) {
        try {
            const body = { item_ids: itemIds, status };
            if (orgId) body.org_id = orgId;
            const options = await this.getFetchOptions('PATCH', body);
            const response = await fetch(`${this.baseUrl}/actions/feed/batch`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error batch updating signal feed:", error);
            return null;
        }
    }

    // ─── Chatbot ────────────────────────────────────────────────────────

    async fetchChatConversations(orgId, limit = 20) {
        try {
            const options = await this.getFetchOptions();
            const response = await fetch(`${this.baseUrl}/chatbot/conversations?org_id=${orgId}&limit=${limit}`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error fetching chat conversations:", error);
            return null;
        }
    }

    async fetchChatConversation(conversationId, orgId) {
        try {
            const options = await this.getFetchOptions();
            const response = await fetch(`${this.baseUrl}/chatbot/conversations/${conversationId}?org_id=${orgId}`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error fetching chat conversation:", error);
            return null;
        }
    }

    async deleteChatConversation(conversationId, orgId) {
        try {
            const options = await this.getFetchOptions('DELETE');
            const response = await fetch(`${this.baseUrl}/chatbot/conversations/${conversationId}?org_id=${orgId}`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error deleting chat conversation:", error);
            return null;
        }
    }

    // Resolve inline citation refs ([{type,id}]) to org-scoped source records for drill-down.
    async resolveChatCitations(orgId, citations) {
        try {
            if (!orgId || !citations?.length) return [];
            const options = await this.getFetchOptions('POST', { org_id: orgId, citations });
            const response = await fetch(`${this.baseUrl}/chatbot/citations/resolve`, options);
            if (this.handleAuthError(response)) return [];
            if (!response.ok) return [];
            const data = await response.json();
            return data?.citations || [];
        } catch (error) {
            console.error("Error resolving chat citations:", error);
            return [];
        }
    }

    // ─── Presend Daily Report Review ─────────────────────────────────────────

    async getPresendReports() {
        try {
            const options = await this.getFetchOptions('GET');
            console.log('[Presend] Fetching:', `${this.baseUrl}/reports/presend`);
            console.log('[Presend] Auth header:', options.headers?.Authorization ? 'Bearer ...' + options.headers.Authorization.slice(-20) : 'MISSING');
            const response = await fetch(`${this.baseUrl}/reports/presend`, options);
            console.log('[Presend] Response status:', response.status, response.statusText);
            if (!response.ok) {
                const text = await response.text();
                console.error('[Presend] Error response body:', text.substring(0, 200));
                return { approvals: [] };
            }
            const data = await response.json();
            console.log('[Presend] Success, approvals count:', data.approvals?.length);
            return data;
        } catch (error) {
            console.error("[Presend] Error fetching presend reports:", error);
            return { approvals: [] };
        }
    }

    // No auth needed for individual report endpoints (approval_id is the token)

    async getPresendReport(approvalId) {
        try {
            const response = await fetch(`${this.baseUrl}/reports/presend/${approvalId}`);
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error fetching presend report:", error);
            return null;
        }
    }

    async updatePresendSummary(approvalId, summaryText) {
        try {
            const response = await fetch(`${this.baseUrl}/reports/presend/${approvalId}/summary`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ summary_text: summaryText }),
            });
            return response.ok;
        } catch (error) {
            console.error("Error updating presend summary:", error);
            return false;
        }
    }

    async updatePresendConnectTheDots(approvalId, connectTheDots) {
        try {
            const response = await fetch(`${this.baseUrl}/reports/presend/${approvalId}/connect-the-dots`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ connect_the_dots: connectTheDots }),
            });
            return response.ok;
        } catch (error) {
            console.error("Error updating presend connect-the-dots:", error);
            return false;
        }
    }

    async presendRemovePost(approvalId, platformId, source, listId) {
        try {
            const response = await fetch(`${this.baseUrl}/reports/presend/${approvalId}/remove-post`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ platform_id: platformId, source, list_id: listId }),
            });
            return response.ok;
        } catch (error) {
            console.error("Error removing presend post:", error);
            return false;
        }
    }

    async presendMovePost(approvalId, platformId, source, fromListId, toListId) {
        try {
            const response = await fetch(`${this.baseUrl}/reports/presend/${approvalId}/move-post`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ platform_id: platformId, source, from_list_id: fromListId, to_list_id: toListId }),
            });
            return response.ok;
        } catch (error) {
            console.error("Error moving presend post:", error);
            return false;
        }
    }

    async presendEditField(approvalId, platformId, source, field, value, listId) {
        try {
            const response = await fetch(`${this.baseUrl}/reports/presend/${approvalId}/edit-field`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ platform_id: platformId, source, field, value, list_id: listId }),
            });
            return response.ok;
        } catch (error) {
            console.error("Error editing presend field:", error);
            return false;
        }
    }

    async regeneratePresendSummary(approvalId) {
        try {
            const response = await fetch(`${this.baseUrl}/reports/presend/${approvalId}/regenerate-summary`, {
                method: 'POST',
            });
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error regenerating presend summary:", error);
            return null;
        }
    }

    async sendPresendReport(approvalId) {
        try {
            const response = await fetch(`${this.baseUrl}/reports/presend/${approvalId}/send`, {
                method: 'POST',
            });
            return await response.json();
        } catch (error) {
            console.error("Error sending presend report:", error);
            return { detail: "Network error" };
        }
    }

    async analyzeArticleForReport(approvalId, url, listId) {
        try {
            const response = await fetch(`${this.baseUrl}/reports/presend/${approvalId}/analyze-article`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ url, list_id: listId }),
            });
            if (!response.ok && response.status !== 409) {
                return { status: 'failed' };
            }
            return await response.json();
        } catch (error) {
            console.error("Error analyzing article for report:", error);
            return { status: 'failed' };
        }
    }

    async previewPresendReport(approvalId) {
        try {
            const response = await fetch(`${this.baseUrl}/reports/presend/${approvalId}/preview`, {
                method: 'POST',
            });
            if (!response.ok) return null;
            return await response.blob();
        } catch (error) {
            console.error("Error previewing presend report:", error);
            return null;
        }
    }

    // ─── Member Daily Reports (read-only) ────────────────────────────────────

    async getDailyReports(orgId) {
        try {
            const options = await this.getFetchOptions('GET');
            const response = await fetch(`${this.baseUrl}/reports/daily?org_id=${orgId}`, options);
            if (!response.ok) return { reports: [] };
            return await response.json();
        } catch (error) {
            console.error("Error fetching daily reports:", error);
            return { reports: [] };
        }
    }

    async getDailyReport(date, orgId, live = false) {
        try {
            const options = await this.getFetchOptions('GET');
            const liveParam = live ? '&live=true' : '';
            const response = await fetch(`${this.baseUrl}/reports/daily/${date}?org_id=${orgId}${liveParam}`, options);
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error fetching daily report:", error);
            return null;
        }
    }

    // Public, login-free shared report (token in the URL). NO auth header — this
    // backs the standalone reports.reactfirst.ai/r/{token} page. Returns
    // { ok, status, data } so the caller can tell "invalid link" (404) from a
    // network error.
    async getSharedReport(token) {
        try {
            const response = await fetch(`${this.baseUrl}/reports/shared/${token}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });
            const data = response.ok ? await response.json() : null;
            return { ok: response.ok, status: response.status, data };
        } catch (error) {
            console.error("Error fetching shared report:", error);
            return { ok: false, status: 0, data: null };
        }
    }

    // Public, login-free: one day's report for a shared token's org. NO auth.
    async getSharedReportDay(token, date) {
        try {
            const response = await fetch(`${this.baseUrl}/reports/shared/${token}/day/${date}`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
            });
            const data = response.ok ? await response.json() : null;
            return { ok: response.ok, status: response.status, data };
        } catch (error) {
            console.error("Error fetching shared report day:", error);
            return { ok: false, status: 0, data: null };
        }
    }

    // ── Strategy API ─────────────────────────────────────────────────────────

    async fetchStrategyBriefing(orgId) {
        try {
            const options = await this.getFetchOptions();
            const response = await fetch(`${this.baseUrl}/strategy/briefing?org_id=${orgId}`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error fetching strategy briefing:", error);
            return null;
        }
    }

    async fetchStrategyBriefings(orgId, limit = 10, offset = 0) {
        try {
            const options = await this.getFetchOptions();
            const response = await fetch(`${this.baseUrl}/strategy/briefings?org_id=${orgId}&limit=${limit}&offset=${offset}`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error fetching strategy briefings:", error);
            return null;
        }
    }

    async generateStrategyBriefing(orgId) {
        try {
            const options = await this.getFetchOptions('POST');
            const response = await fetch(`${this.baseUrl}/strategy/briefing/generate?org_id=${orgId}`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error generating strategy briefing:", error);
            return null;
        }
    }

    async fetchStrategyPatterns(orgId) {
        try {
            const options = await this.getFetchOptions();
            const response = await fetch(`${this.baseUrl}/strategy/patterns?org_id=${orgId}`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error fetching strategy patterns:", error);
            return null;
        }
    }

    async dismissStrategyPattern(patternId, orgId, status = 'dismissed') {
        try {
            const options = await this.getFetchOptions('PATCH', { status });
            const response = await fetch(`${this.baseUrl}/strategy/patterns/${patternId}?org_id=${orgId}`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error dismissing strategy pattern:", error);
            return null;
        }
    }

    async fetchStrategyState(orgId) {
        try {
            const options = await this.getFetchOptions();
            const response = await fetch(`${this.baseUrl}/strategy/state?org_id=${orgId}`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error fetching strategy state:", error);
            return null;
        }
    }

    async fetchStrategyPriorities(orgId) {
        try {
            const options = await this.getFetchOptions();
            const response = await fetch(`${this.baseUrl}/strategy/priorities?org_id=${orgId}`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error fetching strategy priorities:", error);
            return null;
        }
    }

    async setStrategyPriorities(orgId, priorities) {
        try {
            const options = await this.getFetchOptions('POST', { priorities });
            const response = await fetch(`${this.baseUrl}/strategy/priorities?org_id=${orgId}`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error setting strategy priorities:", error);
            return null;
        }
    }

    async acceptSuggestedPriority(orgId, index) {
        try {
            const options = await this.getFetchOptions('POST');
            const response = await fetch(`${this.baseUrl}/strategy/priorities/${index}/accept?org_id=${orgId}`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error accepting suggested priority:", error);
            return null;
        }
    }

    async fetchStrategyEffectiveness(orgId, days = 30) {
        try {
            const options = await this.getFetchOptions();
            const response = await fetch(`${this.baseUrl}/strategy/effectiveness?org_id=${orgId}&days=${days}`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error fetching strategy effectiveness:", error);
            return null;
        }
    }

    // ── Strategy: Goals ──────────────────────────────────────────────────────

    async fetchStrategyGoals(orgId, status = 'active') {
        try {
            const options = await this.getFetchOptions();
            const response = await fetch(`${this.baseUrl}/strategy/goals?org_id=${orgId}&status=${status}`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error fetching strategy goals:", error);
            return null;
        }
    }

    async createStrategyGoal(orgId, data) {
        try {
            const options = await this.getFetchOptions('POST', data);
            const response = await fetch(`${this.baseUrl}/strategy/goals?org_id=${orgId}`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error creating strategy goal:", error);
            return null;
        }
    }

    async updateStrategyGoal(orgId, goalId, data) {
        try {
            const options = await this.getFetchOptions('PATCH', data);
            const response = await fetch(`${this.baseUrl}/strategy/goals/${goalId}?org_id=${orgId}`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error updating strategy goal:", error);
            return null;
        }
    }

    async deleteStrategyGoal(orgId, goalId) {
        try {
            const options = await this.getFetchOptions('DELETE');
            const response = await fetch(`${this.baseUrl}/strategy/goals/${goalId}?org_id=${orgId}`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error deleting strategy goal:", error);
            return null;
        }
    }

    // ── Strategy: Workflows ──────────────────────────────────────────────────

    async fetchStrategyWorkflows(orgId, status = 'active') {
        try {
            const options = await this.getFetchOptions();
            const response = await fetch(`${this.baseUrl}/strategy/workflows?org_id=${orgId}&status=${status}`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error fetching strategy workflows:", error);
            return null;
        }
    }

    async createStrategyWorkflow(orgId, data) {
        try {
            const options = await this.getFetchOptions('POST', data);
            const response = await fetch(`${this.baseUrl}/strategy/workflows?org_id=${orgId}`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error creating strategy workflow:", error);
            return null;
        }
    }

    async updateStrategyWorkflow(orgId, workflowId, data) {
        try {
            const options = await this.getFetchOptions('PATCH', data);
            const response = await fetch(`${this.baseUrl}/strategy/workflows/${workflowId}?org_id=${orgId}`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error updating strategy workflow:", error);
            return null;
        }
    }

    async deleteStrategyWorkflow(orgId, workflowId) {
        try {
            const options = await this.getFetchOptions('DELETE');
            const response = await fetch(`${this.baseUrl}/strategy/workflows/${workflowId}?org_id=${orgId}`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error deleting strategy workflow:", error);
            return null;
        }
    }

    // ── Strategy: Guardrails ─────────────────────────────────────────────────

    async fetchStrategyGuardrails(orgId) {
        try {
            const options = await this.getFetchOptions();
            const response = await fetch(`${this.baseUrl}/strategy/guardrails?org_id=${orgId}`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error fetching strategy guardrails:", error);
            return null;
        }
    }

    async createStrategyGuardrail(orgId, data) {
        try {
            const options = await this.getFetchOptions('POST', data);
            const response = await fetch(`${this.baseUrl}/strategy/guardrails?org_id=${orgId}`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error creating strategy guardrail:", error);
            return null;
        }
    }

    async updateStrategyGuardrail(orgId, guardrailId, data) {
        try {
            const options = await this.getFetchOptions('PATCH', data);
            const response = await fetch(`${this.baseUrl}/strategy/guardrails/${guardrailId}?org_id=${orgId}`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error updating strategy guardrail:", error);
            return null;
        }
    }

    async deleteStrategyGuardrail(orgId, guardrailId) {
        try {
            const options = await this.getFetchOptions('DELETE');
            const response = await fetch(`${this.baseUrl}/strategy/guardrails/${guardrailId}?org_id=${orgId}`, options);
            if (this.handleAuthError(response)) return null;
            if (!response.ok) return null;
            return await response.json();
        } catch (error) {
            console.error("Error deleting strategy guardrail:", error);
            return null;
        }
    }
}

// Create singleton instance
const ListsManager = (() => {
    if (!ListsManagerClass.instance) {
        ListsManagerClass.instance = new ListsManagerClass();
    }
    return ListsManagerClass.instance;
})();

export default ListsManager;